package co.uk.paxes.pinpoint.web

import co.uk.paxes.pinpoint.model.*
import co.uk.paxes.pinpoint.engine.GameEngine
import co.uk.paxes.pinpoint.engine.GameEngineListener
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.*
import org.w3c.dom.events.MouseEvent
import kotlin.js.json
import kotlin.js.Date
import kotlin.random.Random
import kotlin.js.JsExport
import kotlin.js.JsName

// External JS declarations
external val firebase: dynamic

external class Pusher(key: String, options: dynamic) {
    fun subscribe(channel: String): dynamic
    fun unsubscribe(channel: String)
}

// Configuration
const val FIREBASE_API_KEY = "AIzaSyAuSwlSwnP7g7qQb2UIdKdpX9tE5sxkE50"
const val FIREBASE_PROJECT_ID = "pinpoint-1978d"
const val FIREBASE_DATABASE_URL = "https://pinpoint-1978d-default-rtdb.europe-west1.firebasedatabase.app"
const val PUSHER_KEY = "60984f7e5697bc646dee"
const val PUSHER_CLUSTER = "eu"
const val DEBUG = false
const val DEBUG_UNLOCK_ALL = false // Restore normal ad-based unlocking

// Feature flag for new shared board architecture
// When true, internet multiplayer uses SharedBoardManager instead of GameEventManager
// Set to false to use the legacy event-based sync
const val USE_SHARED_BOARD = true  // ENABLED for testing new shared board architecture

// Global State
private var database: dynamic = null
private var pusher: Pusher? = null
private var roomChannel: dynamic = null
private var gameChannel: dynamic = null
private var gameEngine: GameEngine? = null
private var boardSeed: Long? = null
private var gamePlayers: Map<String, Player> = emptyMap()
private var gameCurrentPlayerId: String? = null
private var pendingCurrentPlayerId: String? = null
private var showCurrentPinScore = true
private var showCurrentHandScore = true

// Firebase auth state - tracks if anonymous auth is complete
private var isFirebaseAuthReady = false
private var firebaseAvailable = false

// CRITICAL: Track moves made in current hand (1-4) for proper moveNumber calculation
// This is needed because the moves list is reset after each GameStateUpdate sync
private var currentHandMoveCount: Int = 0

// Score tracking for display - tracks per-player hand scores
private var scoreClearTimeoutId: Int? = null
private var previousTurnPlayerId: String? = null  // Track turn changes to reset pin score display
private var isNewTurn: Boolean = false  // Flag to indicate if this is a fresh turn (no pins placed yet)

// Hand and pin selection state
private var selectedPinIndex: Int = 0
private var pendingJokerPosition: Position? = null
private var pendingJokerPin: Pin? = null

// Undo tracking - stores the last move made by the local player in current hand
private var lastLocalMove: Move? = null
private var lastOriginalPin: Pin? = null  // The original pin from hand BEFORE joker conversion
private var canUndoCurrentMove: Boolean = false
// Store original pin states (position + pin BEFORE reversal) for undo - matches Android's approach
private var lastMoveOriginalPinStates: List<Pair<Position, Pin>> = emptyList()

// Hand completion pause state - 3 second countdown to allow undo at end of hand
private var isInHandCompletionPause: Boolean = false
private var handCompletionPauseTimeRemaining: Int = 0
private var handCompletionPauseTimeoutId: Int? = null
private var lastHandledPauseMoves: Int = -1 // Track which hand completion we've already handled

// Undo tracking for accepting GameStateUpdate from client after undo (like Android)
private var lastUndoPlayerId: String? = null
private var lastUndoTimestamp: Double = 0.0
private const val UNDO_STATE_UPDATE_WINDOW_MS = 2000.0 // Accept GameStateUpdate within 2 seconds of undo

// Score Flash Animation state
private var flashingPositions: Set<Position> = emptySet()
private var isFlashVisible: Boolean = false
private var scoreFlashIntervalId: Int? = null
private var scoreFlashTimeoutId: Int? = null

// Convenience accessors for room state (delegated to FirebaseRoomManager)
private val currentRoomId: String? get() = FirebaseRoomManager.getCurrentRoomId()
private val currentPlayerId: String? get() = FirebaseRoomManager.getCurrentPlayerId()
private val currentPlayerName: String? get() = FirebaseRoomManager.getCurrentPlayerName()
private val isHost: Boolean get() = FirebaseRoomManager.isCurrentPlayerHost()

// Navigation - delegates to NavigationManager
@JsExport
@JsName("navigateTo")
fun navigateTo(screen: String) {
    log("navigateTo called with screen: $screen")
    NavigationManager.navigateTo(screen)
}


@JsExport
@JsName("goBack")
fun goBack() {
    when (NavigationManager.getCurrentScreen()) {
        Screen.ROOM_LIST -> FirebaseRoomManager.stopListeningToRooms()
        else -> { }
    }
    NavigationManager.goBack()
}

/**
 * Setup responsive canvas sizing for mobile devices
 * Resizes all game canvases to fit their containers while maintaining aspect ratio
 */
private fun setupResponsiveCanvas() {
    fun resizeAllCanvases() {
        val canvasIds = listOf("game-board", "ai-game-board", "local-game-board")
        
        for (canvasId in canvasIds) {
            val canvas = document.getElementById(canvasId) as? HTMLCanvasElement ?: continue
            val container = canvas.parentElement ?: continue
            
            // Get container dimensions
            val containerWidth = container.clientWidth - 10
            var containerHeight = container.clientHeight - 10
            
            // Fallback: If container height is too small (might be collapsed), use a portion of window height
            if (containerHeight < 100) {
                containerHeight = (window.innerHeight * 0.5).toInt()
                console.warn("Container $canvasId height too small (${container.clientHeight}), using fallback")
            }
            
            // Calculate the size that fits while maintaining square aspect ratio
            // Max size 500px, but also capped by width/height
            val size = minOf(containerWidth, containerHeight, 500)
            
            if (size > 100) { // Only resize if reasonable size
                canvas.width = size
                canvas.height = size
                console.log("Resized $canvasId to ${size}x${size}")
            }
        }
        
        // Trigger a redraw by dispatching a custom event that game managers can listen to
        window.dispatchEvent(js("new Event('canvasResized')"))
    }
    
    // Initial resize after DOM is ready
    window.setTimeout({
        resizeAllCanvases()
    }, 100)
    
    // Resize on window resize
    window.addEventListener("resize", {
        resizeAllCanvases()
    })
    
    // Also resize when orientation changes (mobile)
    window.addEventListener("orientationchange", {
        window.setTimeout({
            resizeAllCanvases()
        }, 200)
    })
}

// Helper function to wait for Firebase auth before loading rooms
private var authCheckAttempts = 0
private fun waitForAuthAndLoadRooms() {
    // If Firebase is not available (e.g., PlayGama sandbox), load rooms via REST polling immediately
    if (!firebaseAvailable) {
        console.log("⚡ Firebase not available, loading rooms via REST polling")
        FirebaseRoomManager.startListeningToRooms()
        return
    }
    if (isFirebaseAuthReady) {
        console.log("✅ Auth ready, loading rooms")
        FirebaseRoomManager.startListeningToRooms()
        authCheckAttempts = 0
    } else {
        authCheckAttempts++
        if (authCheckAttempts < 50) {
            console.log("⏳ Waiting for Firebase auth... attempt $authCheckAttempts")
            window.setTimeout({ waitForAuthAndLoadRooms() }, 100)
        } else {
            console.error("❌ Auth timeout - could not authenticate")
            window.alert("Connection timeout. Please refresh the page.")
            authCheckAttempts = 0
        }
    }
}

private fun setupNavigationCallbacks() {
    NavigationManager.onScreenChange = { newScreen, _ ->
        when (newScreen) {
            Screen.ROOM_LIST -> {
                // Wait for Firebase auth before loading rooms
                waitForAuthAndLoadRooms()
                // Load saved player name
                val savedName = window.localStorage.getItem("pinpoint_web_player_name")
                if (savedName != null) {
                    window.setTimeout({
                        val input = document.getElementById("player-name") as? HTMLInputElement
                        if (input != null && input.value.isEmpty()) {
                            input.value = savedName
                        }
                    }, 100)
                }
            }
            Screen.SETTINGS -> loadSettingsUI()
            Screen.LOCAL_SETUP -> {
                // Initialize player slots when navigating to setup
                val initFn = window.asDynamic().initLocalSetup
                if (initFn != null) initFn()
            }
            Screen.LEADERBOARD -> loadLeaderboard()
            Screen.MAIN_MENU, Screen.LANDING -> {
                // Check for saved game on main menu and landing screen
                checkForSavedGame()
            }
            else -> { }
        }
    }
}

private fun setupRoomManagerCallbacks() {
    FirebaseRoomManager.onRoomListUpdated = { rooms -> renderRoomList(rooms) }
    FirebaseRoomManager.onRoomStateChanged = { roomState -> updateLobbyUI(roomState) }
    FirebaseRoomManager.onGameStarted = { roomState ->
        if (NavigationManager.getCurrentScreen() == Screen.ROOM_LOBBY) {
            onGameStarted(roomState)
        }
    }
}

private fun renderRoomList(rooms: List<RoomInfo>) {
    val container = document.getElementById("room-list") ?: return
    if (rooms.isEmpty()) {
        container.innerHTML = "<div class=\"loading\">No rooms available</div>"
        return
    }
    val html = StringBuilder()
    for (room in rooms) {
        val statusClass = if (room.gameStarted) "in-progress" else "waiting"
        val statusText = if (room.gameStarted) "In Progress" else "Waiting"
        val clickable = if (room.isFull) "" else "onclick=\"joinRoom('${room.id}')\""
        val fullClass = if (room.isFull) " room-full" else ""
        html.append("<div class=\"room-item $statusClass$fullClass\" $clickable>")
        html.append("<div class=\"room-name\">${room.name}</div>")
        html.append("<div class=\"room-info\">${room.playerCount}/${room.maxPlayers} players ")
        html.append("<span class=\"room-status $statusClass\">$statusText</span></div></div>")
    }
    container.innerHTML = html.toString()
}

private fun updateLobbyUI(roomState: RoomState) {
    val container = document.getElementById("lobby-players") ?: return
    val startBtn = document.getElementById("btn-start-game") as? HTMLButtonElement
    val statusEl = document.getElementById("lobby-status")
    val roomNameEl = document.getElementById("lobby-room-name")
    
    // Update room name
    roomNameEl?.textContent = roomState.name
    
    val html = StringBuilder()
    
    // Show existing players
    for ((playerId, player) in roomState.players) {
        val badge = if (player.host) "<span class=\"player-badge\">Host</span>" else ""
        val aiEmoji = if (player.isAI) "🤖 " else ""
        val aiDifficulty = if (player.isAI) " <span class=\"ai-difficulty\">${player.aiDifficulty ?: "EASY"}</span>" else ""
        val removeBtn = if (isHost && player.isAI) " <button class=\"btn-remove-ai\" onclick=\"removeAIPlayer('$playerId')\">✕</button>" else ""
        
        html.append("<div class=\"player-item\">")
        html.append("<div class=\"player-info\">")
        html.append("<span class=\"player-name\">$aiEmoji${player.name}</span>")
        html.append("<div class=\"player-badges\">$badge$aiDifficulty</div>")
        html.append("</div>")
        html.append("$removeBtn")
        html.append("</div>")
    }
    
    // Show empty slots with "Add AI" option for host
    val emptySlots = roomState.maxPlayers - roomState.players.size
    val humanCount = roomState.players.values.count { player -> !player.isAI }
    val canAddAI = isHost && emptySlots > 0 && humanCount >= 1
    
    for (i in 0 until emptySlots) {
        if (i == 0 && canAddAI) {
            // First empty slot shows "Add AI" button for host
            html.append("<div class=\"player-item add-ai-slot\">")
            html.append("<div class=\"player-info\">")
            html.append("<span class=\"player-name\">Waiting for player...</span>")
            html.append("</div>")
            html.append("<button class=\"btn-add-ai\" onclick=\"document.getElementById('ai-difficulty-dialog').style.display='flex'\">🤖 Add AI</button>")
            html.append("</div>")
        } else {
            // Regular empty slot
            html.append("<div class=\"player-item empty-slot\">")
            html.append("<span class=\"player-name\">Waiting for player...</span>")
            html.append("</div>")
        }
    }
    
    container.innerHTML = html.toString()
    
    // Update start button and status
    val playerCount = roomState.players.size
    when {
        isHost && playerCount >= 2 -> {
            startBtn?.style?.display = "block"
            statusEl?.textContent = "Ready to start!"
        }
        isHost -> {
            startBtn?.style?.display = "none"
            statusEl?.textContent = "Waiting for more players..."
        }
        else -> {
            startBtn?.style?.display = "none"
            statusEl?.textContent = "Waiting for host to start..."
        }
    }
}


@JsExport
@JsName("joinRoom")
fun joinRoom(roomId: String) {
    val input = document.getElementById("player-name") as? HTMLInputElement
    val name = input?.value?.trim() ?: ""
    if (name.isEmpty()) {
        window.alert("Please enter your name")
        input?.focus()
        return
    }
    // Save name to localStorage
    window.localStorage.setItem("pinpoint_web_player_name", name)
    
    FirebaseRoomManager.joinRoom(roomId, name) { success, error ->
        if (success) {
            subscribeToPusher(roomId)
            NavigationManager.navigateTo(Screen.ROOM_LOBBY)
        } else {
            window.alert(error ?: "Failed to join room")
        }
    }
}

private fun subscribeToPusher(roomId: String) {
    val p = pusher ?: return
    roomChannel = p.subscribe("pinpoint-room-$roomId")
    gameChannel = p.subscribe("pinpoint-game-$roomId")
    
    // Note: With SharedBoardManager, all game state sync happens via Firebase listeners
    // Pusher is only used for room management (join/leave notifications)
    // The game event handlers below are no longer needed
    console.log("Pusher subscribed to room-$roomId and game-$roomId (Firebase handles game sync)")
}

private fun unsubscribeFromPusher(roomId: String) {
    pusher?.unsubscribe("pinpoint-room-$roomId")
    pusher?.unsubscribe("pinpoint-game-$roomId")
    roomChannel = null
    gameChannel = null
}


private fun setupGameEventManagerCallbacks() {
    // No longer needed - SharedBoardManager handles all game sync via Firebase
    console.log("setupGameEventManagerCallbacks: SKIPPED (using SharedBoardManager)")
}

@JsExport
@JsName("startGame")
fun startGame() {
    FirebaseRoomManager.startGame { success, error ->
        if (!success) window.alert(error ?: "Failed to start game")
    }
}


private fun onGameStarted(roomState: RoomState) {
    NavigationManager.navigateTo(Screen.GAME)
    val gameInfo = roomState.gameInfo
    val seed = gameInfo?.boardSeed ?: Date.now().toLong()
    
    // ========================================
    // NEW SHARED BOARD ARCHITECTURE (when enabled)
    // ========================================
    if (USE_SHARED_BOARD) {
        console.log("🆕 Using new SharedBoardManager architecture")
        
        // Initialize SharedBoardManager for this game
        val playerId = currentPlayerId ?: return
        val playerName = currentPlayerName ?: "Unknown"
        
        // Use players from the room state - this should have all players who joined
        val allPlayers = roomState.players.values.map { info ->
            SharedBoardManager.PlayerInitInfo(
                id = info.id,
                name = info.name,
                isAI = info.isAI,
                aiDifficulty = info.aiDifficulty
            )
        }
        
        console.log("🎮 Starting game with ${allPlayers.size} players:")
        allPlayers.forEachIndexed { index, playerInfo ->
            console.log("  Player $index: ${playerInfo.id} (${playerInfo.name}, isAI=${playerInfo.isAI})")
        }
        
        // Set up connection status monitoring
        SharedBoardManager.onConnectionChanged = { connected ->
            updateConnectionIndicator(connected)
        }
        
        // Set up InternetGameManager callbacks
        InternetGameManager.onBoardUpdated = {
            renderBoardFromSharedState()
            renderScoresFromSharedState()
            renderHandFromSharedState()
            updateTurnIndicatorFromSharedState()
            updateUndoButtonFromSharedState()  // Update undo button after every board change
        }
        
        InternetGameManager.onTurnChanged = { newPlayerId ->
            console.log("🔄 Turn changed to: $newPlayerId")
            gameCurrentPlayerId = newPlayerId
            
            // Set flag to indicate this is a fresh turn with no pins placed yet
            isNewTurn = true
            
            updateTurnIndicatorFromSharedState()
            renderHandFromSharedState()
            updateUndoButtonFromSharedState()  // Update undo button when turn changes
            
            // Re-render scores to show reset pin score
            renderScoresFromSharedState()
            
            // Update button visibility based on player type - add delay to ensure player data is loaded
            window.setTimeout({
                console.log("🔄 Calling TurnBasedUIManager.onTurnChanged with delay for player: $newPlayerId")
                TurnBasedUIManager.onTurnChanged(newPlayerId)
            }, 100)
        }
        
        InternetGameManager.onPinPlaced = {
            isNewTurn = false
            // Re-render scores to show actual pin score
            renderScoresFromSharedState()
        }
        
        InternetGameManager.onGamePhaseChanged = { phase ->
            console.log("🎮 Game phase changed to: $phase")
            if (phase == "PAUSED") {
                // Show undo button during pause
                updateUndoButtonFromSharedState()
            }
        }
        
        InternetGameManager.onGameEnded = { scores, winnerId ->
            console.log("🏆 Game ended! Winner: $winnerId")
            
            // Show interstitial ad on game end
            BannerAdManager.showInterstitialAd {
                console.log("✅ Interstitial ad closed, showing game over screen")
                displayGameOverFromSharedState(scores, winnerId)
            }
        }
        
        InternetGameManager.onError = { error ->
            console.error("❌ InternetGameManager error: $error")
            window.alert("Game error: $error")
        }
        
        // Trigger flash animation when scoring positions change
        InternetGameManager.onScoringPositionsChanged = { positions ->
            console.log("⚡ Triggering score flash for shared board: ${positions.size} positions")
            // Add small delay to ensure board is fully rendered before flash starts
            window.setTimeout({
                triggerScoreFlashForSharedBoard(positions)
            }, 50)
        }
        
        // Get max undos setting from SettingsManager
        val maxUndos = SettingsManager.getMaxUndosPerHand()
        console.log("🎮 Starting game with maxUndosPerHand=$maxUndos")
        
        // Start the game
        InternetGameManager.startGame(roomState.id, playerId, playerName, isHost, allPlayers, maxUndos)
        
        // Initialize turn-based UI management
        TurnBasedUIManager.initialize()
        
        // Set up board click handler for shared board mode
        setupBoardClickHandlerForSharedBoard()
        
        // Ensure audio is unlocked - show tap prompt if needed
        ensureAudioUnlocked()
        
        // SharedBoardManager handles all game sync - no legacy code needed
    }
}

@JsExport
@JsName("forceResetRoom")
fun forceResetRoom() {
    val roomId = currentRoomId ?: FirebaseRoomManager.getCurrentRoomId() ?: return
    if (window.confirm("Are you sure? This will kick all players and reset the room state to clear any 'ghost' players.")) {
        FirebaseRoomManager.forceResetRoom(roomId) {
            window.location.reload()
        }
    }
}

/**
 * Clear all rooms and game data from Firebase (debug function).
 * Recreates the 6 fixed rooms after clearing.
 */
fun clearAllRoomsDebug() {
    console.log("🗑️ Clearing all rooms and game data...")
    
    val db = database
    if (db == null) {
        console.error("Database not initialized")
        window.alert("Database not initialized")
        return
    }
    
    // Clear games
    db.ref("games").remove()
        .then {
            console.log("✅ Cleared games")
        }
        .catch { error: dynamic ->
            console.error("❌ Failed to clear games: ${error.message}")
        }
    
    // Clear game-events
    db.ref("game-events").remove()
        .then {
            console.log("✅ Cleared game-events")
        }
        .catch { error: dynamic ->
            console.error("❌ Failed to clear game-events: ${error.message}")
        }
    
    // Clear fixed-rooms and recreate them
    db.ref("fixed-rooms").remove()
        .then {
            console.log("✅ Cleared fixed-rooms, now recreating...")
            recreateFixedRooms()
        }
        .catch { error: dynamic ->
            console.error("❌ Failed to clear fixed-rooms: ${error.message}")
        }
}

/**
 * Recreate the 6 fixed rooms in Firebase.
 */
private fun recreateFixedRooms() {
    val db = database ?: return
    
    val roomNames = listOf("Alpha", "Beta", "Charlie", "Delta", "Echo", "Foxtrot")
    val roomIds = listOf("room-alpha", "room-beta", "room-charlie", "room-delta", "room-echo", "room-foxtrot")
    
    val roomsData = js("{}")
    
    roomIds.forEachIndexed { index, roomId ->
        roomsData[roomId] = json(
            "id" to roomId,
            "name" to "Room ${roomNames[index]}",
            "players" to js("{}"),
            "gameStarted" to false,
            "maxPlayers" to 4,
            "createdAt" to Date.now().toLong()
        )
    }
    
    db.ref("fixed-rooms").set(roomsData)
        .then {
            console.log("✅ Recreated 6 fixed rooms with maxPlayers=4")
            window.alert("Rooms cleared and recreated! Refreshing...")
            window.setTimeout({
                window.location.reload()
            }, 500)
        }
        .catch { error: dynamic ->
            console.error("❌ Failed to recreate rooms: ${error.message}")
            window.alert("Failed to recreate rooms: ${error.message}")
        }
}

@JsExport
@JsName("leaveRoom")
fun leaveRoom() {
    console.log("leaving room called...")
    val roomId = currentRoomId
    console.log("Leaving room: $roomId")
    
    // Stop any active game processes
    scoreFlashIntervalId?.let { window.clearInterval(it) }
    scoreFlashTimeoutId?.let { window.clearTimeout(it) }
    
    // Clean up shared board architecture
    InternetGameManager.cleanup()
    
    // Clean up turn-based UI manager
    TurnBasedUIManager.cleanup()
    
    // Clear game data from Firebase to prevent database bloat
    if (roomId != null) {
        try {
            // Clean up shared board game data if host
            if (isHost) {
                SharedBoardManager.deleteGame(roomId)
            }
            unsubscribeFromPusher(roomId)
        } catch (e: Exception) {
            console.error("Error clearing game events: ${e.message}")
        }
    }
    
    // Set a timeout to force exit if Firebase hangs
    val forceExitId = window.setTimeout({
        console.warn("Firebase leave callback timed out - forcing exit")
        gameEngine = null
        goBack()
        window.location.reload() // Force reload to clear state cleanly
    }, 2000)
    
    FirebaseRoomManager.leaveRoom { 
        console.log("Firebase leave callback received")
        window.clearTimeout(forceExitId)
        gameEngine = null
        goBack()
        window.location.reload() // Force reload to clear state cleanly
    }
}


// Legacy event handlers removed - using SharedBoardManager architecture now


private fun displayGameOverScreen(winnerId: String?, finalScores: Map<String, Int>) {
    val dialog = document.getElementById("game-over-dialog") ?: return
    val winnerDisplay = document.getElementById("game-over-winner")
    val scoresList = document.getElementById("game-over-scores")
    
    val winnerName = if (winnerId != null) gamePlayers[winnerId]?.name ?: "Unknown" else "Tie"
    
    // Winner display
    if (winnerDisplay != null) {
        val isWinner = winnerId == currentPlayerId
        val winnerHtml = StringBuilder()
        if (winnerId != null) {
            winnerHtml.append("<span class=\"winner-label\">Winner</span>")
            winnerHtml.append("<span class=\"winner-name\">${if (isWinner) "🎉 YOU WIN 🎉" else winnerName}</span>")
        } else {
            winnerHtml.append("<span class=\"winner-name\">TIE GAME</span>")
        }
        winnerDisplay.innerHTML = winnerHtml.toString()
    }
    
    // Scores list
    if (scoresList != null) {
        val scoresHtml = StringBuilder()
        for ((playerId, score) in finalScores.entries.sortedByDescending { it.value }) {
            val playerName = gamePlayers[playerId]?.name ?: playerId
            val isMe = playerId == currentPlayerId
            scoresHtml.append("<div class=\"final-score-row ${if (isMe) "is-me" else ""}\">")
            scoresHtml.append("<span class=\"player-name\">$playerName</span>")
            scoresHtml.append("<span class=\"player-score\">$score</span>")
            scoresHtml.append("</div>")
        }
        scoresList.innerHTML = scoresHtml.toString()
    }
    
    dialog.asDynamic().style.display = "flex"
    
    // Also update turn indicator as fallback
    val indicator = document.getElementById("game-turn-indicator")
    if (indicator != null) {
        indicator.innerHTML = "Game Over"
        indicator.classList.remove("my-turn")
        indicator.classList.add("game-over-indicator")
    }
    
    val canvas = document.getElementById("game-board") as? HTMLCanvasElement
    canvas?.style?.cursor = "default"
}

// Pin colors matching Android app (lighter, more vibrant)
private val PIN_COLORS = mapOf(
    PinColor.RED to "#FF6B6B",      // Lighter, more coral red
    PinColor.GREEN to "#00FF00",     // Bright green
    PinColor.PURPLE to "#8B00FF",    // More vibrant purple (violet)
    PinColor.YELLOW to "#FFFF00",    // Bright yellow
    PinColor.BLACK to "#000000"      // Black
)

// Section dividers pass through the CENTER of circles at columns/rows 3 and 6
private val SECTION_DIVIDER_COLS = setOf(3, 6)
private val SECTION_DIVIDER_ROWS = setOf(3, 6)

// Board colors
private const val BOARD_BACKGROUND = "#2E2E2E"  // Dark board surface
private const val EMPTY_HOLE_COLOR = "#757575"   // Pale grey for empty holes
private const val GRID_LINE_COLOR = "#00FFFF"    // Cyan grid lines


private fun renderBoard() {
    val canvas = document.getElementById("game-board") as? HTMLCanvasElement ?: return
    val ctx = canvas.getContext("2d") as CanvasRenderingContext2D
    val cellSize = canvas.width / 10.0
    
    // Dark board background
    ctx.fillStyle = BOARD_BACKGROUND
    ctx.fillRect(0.0, 0.0, canvas.width.toDouble(), canvas.height.toDouble())
    
    // Draw grid lines FIRST (behind holes)
    drawGridLines(ctx, cellSize, canvas.width.toDouble(), canvas.height.toDouble())
    
    val state = gameEngine?.getCurrentGameState()
    
    // Debug: Log pins on board
    // [SYNC-DEBUG] Rendered pins after update:
    var renderedCount = 0
    for (row in 0 until 10) {
        for (col in 0 until 10) {
            val position = Position.fromLegacy(row, col)
            val pin = state?.board?.getPinAt(position)
            if (pin != null) {
                renderedCount++
                if (renderedCount <= 20) {
                    console.log("[SYNC-DEBUG] Rendered pin at (", row, ",", col, "): color=", pin.color, ", joker=", pin.isJoker, ", reversed=", pin.isReversed, ", jokerTurnedBlack=", pin.isJokerTurnedBlack);
                }
            }
        }
    }
    console.log("[SYNC-DEBUG] Total pins rendered on board: ", renderedCount);
    var pinCount = 0
    for (row in 0 until 10) {
        for (col in 0 until 10) {
            val position = Position.fromLegacy(row, col)
            val pin = state?.board?.getPinAt(position)
            if (pin != null) {
                pinCount++
                if (pinCount <= 10) {  // Only log first 10 pins
                    console.log("Board pin at ($row,$col): ${pin.color}, joker=${pin.isJoker}, reversed=${pin.isReversed}")
                }
            }
        }
    }
    console.log("Total pins on board: $pinCount")
    
    // Draw all cells (empty holes and pins) on top of grid lines
    for (row in 0 until 10) {
        for (col in 0 until 10) {
            val position = Position.fromLegacy(row, col)
            val pin = state?.board?.getPinAt(position)
            drawCell(ctx, row, col, pin, cellSize)
        }
    }
}

private fun drawCell(ctx: CanvasRenderingContext2D, row: Int, col: Int, pin: Pin?, cellSize: Double, isScoring: Boolean = false) {
    val x = col * cellSize + cellSize / 2
    val y = row * cellSize + cellSize / 2
    val holeRadius = cellSize * 0.42  // 85% of half cell = hole size
    val pinRadius = cellSize * 0.32   // 65% of half cell = pin size
    
    // Draw the hole (grey circle background)
    ctx.beginPath()
    ctx.arc(x, y, holeRadius, 0.0, 2 * kotlin.math.PI)
    ctx.fillStyle = EMPTY_HOLE_COLOR
    ctx.fill()
    ctx.strokeStyle = "#424242"  // Slightly lighter border
    ctx.lineWidth = 0.5
    ctx.stroke()
    
    // Draw the pin if present
    if (pin != null) {
        val effectiveColor = pin.getEffectiveColor()
        val fillColor = PIN_COLORS[effectiveColor] ?: "#666"
        
        ctx.beginPath()
        ctx.arc(x, y, pinRadius, 0.0, 2 * kotlin.math.PI)
        ctx.fillStyle = fillColor
        ctx.fill()
        
        // Simple border for all pins
        ctx.strokeStyle = "rgba(0, 0, 0, 0.3)"
        ctx.lineWidth = 0.5
        ctx.stroke()
        
        // Joker indicator - always show J for jokers (even when turned black)
        if (pin.isJoker) {
            ctx.fillStyle = "#fff"
            ctx.font = "bold ${(cellSize * 0.35).toInt()}px Arial"
            ctx.textAlign = CanvasTextAlign.CENTER
            ctx.textBaseline = CanvasTextBaseline.MIDDLE
            ctx.fillText("J", x, y)
        }
        
        // Note: Reversed pins just show as BLACK via getEffectiveColor() - no special indicator needed
        
        // Draw white ring around scoring pins
        if (isScoring) {
            ctx.beginPath()
            ctx.arc(x, y, pinRadius * 1.25, 0.0, 2 * kotlin.math.PI)
            ctx.strokeStyle = "#FFFFFF"
            ctx.lineWidth = 3.0
            ctx.stroke()
        }
    }
    
    // Draw flash highlight if active for this position
    if (flashingPositions.contains(Position.fromLegacy(row, col)) && isFlashVisible) {
        // Draw flash around the PIN (not the hole) for better visibility
        val flashRadius = if (pin != null) pinRadius * 1.2 else holeRadius * 1.1
        
        ctx.beginPath()
        ctx.arc(x, y, flashRadius, 0.0, 2 * kotlin.math.PI)
        ctx.strokeStyle = "#FFFFFF"
        ctx.lineWidth = 4.0
        ctx.stroke()
        
        // Add a subtle outer glow
        ctx.beginPath()
        ctx.arc(x, y, flashRadius * 1.15, 0.0, 2 * kotlin.math.PI)
        ctx.strokeStyle = "rgba(255, 255, 255, 0.5)"
        ctx.lineWidth = 2.0
        ctx.stroke()
    }

}

private fun drawGridLines(ctx: CanvasRenderingContext2D, cellSize: Double, width: Double, height: Double) {
    ctx.strokeStyle = GRID_LINE_COLOR
    ctx.lineWidth = 3.0
    
    // Vertical lines pass through the CENTER of circles at columns 3 and 6
    for (col in SECTION_DIVIDER_COLS) {
        val x = col * cellSize + cellSize / 2  // Center of the cell
        ctx.beginPath()
        ctx.moveTo(x, 0.0)
        ctx.lineTo(x, height)
        ctx.stroke()
    }
    
    // Horizontal lines pass through the CENTER of circles at rows 3 and 6
    for (row in SECTION_DIVIDER_ROWS) {
        val y = row * cellSize + cellSize / 2  // Center of the cell
        ctx.beginPath()
        ctx.moveTo(0.0, y)
        ctx.lineTo(width, y)
        ctx.stroke()
    }
}

/**
 * Draw a cell for shared board mode with flash animation support.
 */
private fun drawCellForSharedBoard(ctx: CanvasRenderingContext2D, row: Int, col: Int, pin: Pin?, cellSize: Double, isFlashing: Boolean = false) {
    val x = col * cellSize + cellSize / 2
    val y = row * cellSize + cellSize / 2
    val holeRadius = cellSize * 0.42
    val pinRadius = cellSize * 0.32
    
    // Draw the hole (grey circle background)
    ctx.beginPath()
    ctx.arc(x, y, holeRadius, 0.0, 2 * kotlin.math.PI)
    ctx.fillStyle = EMPTY_HOLE_COLOR
    ctx.fill()
    ctx.strokeStyle = "#424242"
    ctx.lineWidth = 0.5
    ctx.stroke()
    
    // Draw the pin if present
    if (pin != null) {
        // Use getEffectiveColor() for all pins - handles jokers, reversed pins, and black jokers correctly
        val displayColor = pin.getEffectiveColor()
        val fillColor = PIN_COLORS[displayColor] ?: "#666"
        
        ctx.beginPath()
        ctx.arc(x, y, pinRadius, 0.0, 2 * kotlin.math.PI)
        ctx.fillStyle = fillColor
        ctx.fill()
        
        // Simple border for all pins
        ctx.strokeStyle = "rgba(0, 0, 0, 0.3)"
        ctx.lineWidth = 0.5
        ctx.stroke()
        
        // Joker indicator - show "J" on jokers
        if (pin.isJoker) {
            ctx.fillStyle = "#fff"
            ctx.font = "bold ${(cellSize * 0.35).toInt()}px Arial"
            ctx.textAlign = CanvasTextAlign.CENTER
            ctx.textBaseline = CanvasTextBaseline.MIDDLE
            ctx.fillText("J", x, y)
        }
        
        // Draw white flash ring around scoring pins
        if (isFlashing) {
            ctx.beginPath()
            ctx.arc(x, y, pinRadius * 1.3, 0.0, 2 * kotlin.math.PI)
            ctx.strokeStyle = "#FFFFFF"
            ctx.lineWidth = 4.0
            ctx.stroke()
            
            // Add outer glow
            ctx.beginPath()
            ctx.arc(x, y, pinRadius * 1.45, 0.0, 2 * kotlin.math.PI)
            ctx.strokeStyle = "rgba(255, 255, 255, 0.5)"
            ctx.lineWidth = 2.0
            ctx.stroke()
        }
    }
}

// ========================================
// SHARED BOARD RENDERING FUNCTIONS
// These are used when USE_SHARED_BOARD is enabled
// ========================================

// Flash animation state for shared board mode
private var sharedBoardFlashingPositions: Set<String> = emptySet()
private var sharedBoardFlashVisible: Boolean = false
private var sharedBoardFlashIntervalId: Int? = null
private var sharedBoardFlashTimeoutId: Int? = null

/**
 * Update the connection status indicator.
 * Shows a red banner when disconnected from Firebase.
 */
private fun updateConnectionIndicator(connected: Boolean) {
    val existingIndicator = document.getElementById("connection-indicator")
    
    if (connected) {
        // Remove disconnection banner if it exists
        existingIndicator?.remove()
        console.log("🟢 Connection indicator: CONNECTED")
    } else {
        // Show disconnection banner
        if (existingIndicator == null) {
            val indicator = document.createElement("div")
            indicator.id = "connection-indicator"
            indicator.asDynamic().style.cssText = """
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                background: #f44336;
                color: white;
                text-align: center;
                padding: 8px;
                font-size: 14px;
                z-index: 9998;
                font-weight: bold;
            """.trimIndent().replace("\n", " ")
            indicator.textContent = "⚠️ Disconnected - Reconnecting..."
            document.body?.insertBefore(indicator, document.body?.firstChild)
        }
        console.log("🔴 Connection indicator: DISCONNECTED")
    }
}

/**
 * Ensure audio is unlocked on mobile browsers.
 * Shows a tap overlay on mobile/tablet devices only.
 * Desktop browsers typically don't need this.
 * 
 * iOS Safari and Android Chrome require audio to be played from a direct user gesture.
 * The overlay ensures the user taps, which triggers audio unlock.
 */
private fun ensureAudioUnlocked() {
    // Initialize sound manager if not already done
    SoundManager.initialize()
    
    // Check if this is a mobile/tablet device
    val userAgent = window.navigator.userAgent.lowercase()
    val isMobile = userAgent.contains("mobile") || 
                   userAgent.contains("android") || 
                   userAgent.contains("iphone") || 
                   userAgent.contains("ipad") ||
                   userAgent.contains("ipod") ||
                   userAgent.contains("tablet")
    
    // Also check for touch capability as a fallback
    val hasTouch = js("'ontouchstart' in window || navigator.maxTouchPoints > 0") as Boolean
    val isMobileOrTouch = isMobile || hasTouch
    
    if (!isMobileOrTouch) {
        console.log("🔊 ensureAudioUnlocked: Desktop detected, skipping overlay")
        // Still try to unlock audio in case it's needed
        SoundManager.unlockAudio()
        return
    }
    
    console.log("🔊 ensureAudioUnlocked: Mobile/touch device detected (userAgent=$userAgent, hasTouch=$hasTouch)")
    console.log("🔊 Showing audio unlock overlay - user MUST tap to enable sound")
    
    // Remove any existing overlay
    val existingOverlay = document.getElementById("audio-unlock-overlay")
    existingOverlay?.remove()
    
    val overlay = document.createElement("div")
    overlay.id = "audio-unlock-overlay"
    overlay.asDynamic().style.cssText = """
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.85);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 9999;
        cursor: pointer;
        -webkit-tap-highlight-color: transparent;
        transition: opacity 0.5s ease;
    """.trimIndent().replace("\n", " ")
    
    val message = document.createElement("div")
    message.asDynamic().style.cssText = """
        color: white;
        font-size: 28px;
        text-align: center;
        padding: 50px;
        background: rgba(0, 0, 0, 0.9);
        border-radius: 20px;
        border: 3px solid #4CAF50;
        box-shadow: 0 0 30px rgba(76, 175, 80, 0.5);
    """.trimIndent().replace("\n", " ")
    message.innerHTML = "🔊<br><br>Tap anywhere to<br>enable game sounds"
    
    overlay.appendChild(message)
    document.body?.appendChild(overlay)
    
    var dismissed = false
    
    // This function MUST be called from a direct user gesture (tap/click)
    val dismissOverlay: () -> Unit = {
        console.log("🔊 Interaction detected for dismissal (dismissed=$dismissed)")
        if (!dismissed) {
            dismissed = true
            console.log("🔊 Audio unlock overlay: Starting dismissal sequence...")
            
            // 1. Attempt to unlock audio - wrap tightly to prevent blockage
            try {
                console.log("🔊 Attempting SoundManager.unlockAudio()...")
                SoundManager.unlockAudio()
                console.log("🔊 Attempting SoundManager.playTestSound()...")
                SoundManager.playTestSound()
            } catch (e: Exception) {
                console.warn("⚠️ Audio unlock interrupted, continuing dismissal: ${e.message}")
            }
            
            // 2. Provide visual feedback - defensive style updates
            try {
                console.log("🔊 Updating overlay message...")
                message.innerHTML = "✅<br><br>Sound Enabled!"
                val msgStyle = message.asDynamic().style
                msgStyle.borderColor = "#4CAF50"
                msgStyle.boxShadow = "0 0 50px rgba(76, 175, 80, 0.8)"
            } catch (e: Exception) {
                console.warn("⚠️ Visual feedback style update failed: ${e.message}")
            }
            
            // 3. Guaranteed removal after delay
            console.log("🔊 Scheduling overlay removal...")
            window.setTimeout({
                console.log("🔊 Starting overlay fade-out...")
                try {
                    overlay.asDynamic().style.opacity = "0"
                    overlay.asDynamic().style.pointerEvents = "none"
                } catch (e: Exception) {
                    console.warn("⚠️ Overlay fade-out style update failed: ${e.message}")
                }
                
                window.setTimeout({
                    try {
                        if (overlay.parentNode != null) {
                            overlay.remove()
                            console.log("🔊 Audio unlock overlay successfully removed from DOM")
                        } else {
                            console.log("🔊 Audio unlock overlay already removed")
                        }
                    } catch (e: Exception) {
                        console.error("❌ Critical: Failed to remove overlay from DOM: ${e.message}")
                    }
                }, 500)
            }, 2000)
        }
    }
    
    // Remove overlay on any interaction - these are direct user gestures
    // Use multiple event types for maximum compatibility
    val interactionEvents = listOf("click", "touchstart", "touchend")
    interactionEvents.forEach { type ->
        overlay.addEventListener(type, { 
            console.log("🔊 Overlay interaction: $type")
            dismissOverlay() 
        })
    }
    
    // Final fallback: Remove overlay after 30 seconds no matter what
    window.setTimeout({
        if (overlay.parentNode != null) {
            console.warn("⚠️ Overlay safety timeout triggered - forcing removal")
            overlay.remove()
        }
    }, 30000)
}

/**
 * Trigger the score flash animation for shared board mode.
 * Positions are in "row_col" format.
 */
private fun triggerScoreFlashForSharedBoard(positions: List<String>) {
    console.log("⚡⚡⚡ triggerScoreFlashForSharedBoard CALLED with ${positions.size} positions: $positions")
    
    if (positions.isEmpty()) {
        console.log("⚡ triggerScoreFlashForSharedBoard: No positions to flash")
        return
    }
    
    val newPositionsSet = positions.toSet()
    
    // ALWAYS restart the flash animation when called
    // This ensures mobile clients see the flash even if they receive duplicate updates
    // The previous "optimization" to skip if already flashing was causing issues on mobile
    
    console.log("⚡⚡⚡ STARTING FLASH for ${positions.size} pins: $positions")
    
    // Cancel any existing flash
    sharedBoardFlashIntervalId?.let { 
        console.log("⚡ Clearing existing flash interval: $it")
        window.clearInterval(it) 
    }
    sharedBoardFlashTimeoutId?.let { 
        console.log("⚡ Clearing existing flash timeout: $it")
        window.clearTimeout(it) 
    }
    
    sharedBoardFlashingPositions = newPositionsSet
    sharedBoardFlashVisible = true
    console.log("⚡ Flash positions set: $sharedBoardFlashingPositions, visible: $sharedBoardFlashVisible")
    
    // Initial render with flash visible
    renderBoardFromSharedState()
    
    // Toggle flash every 300ms for 2 seconds
    var toggleCount = 0
    sharedBoardFlashIntervalId = window.setInterval({
        sharedBoardFlashVisible = !sharedBoardFlashVisible
        toggleCount++
        console.log("⚡ Flash toggle #$toggleCount: visible=$sharedBoardFlashVisible, positions=${sharedBoardFlashingPositions.size}")
        renderBoardFromSharedState()
    }, 300)
    
    console.log("⚡ Flash interval started with ID: $sharedBoardFlashIntervalId")
    
    // End flash after 2 seconds
    sharedBoardFlashTimeoutId = window.setTimeout({
        console.log("⚡ Flash timeout fired - ending flash animation")
        sharedBoardFlashIntervalId?.let { window.clearInterval(it) }
        sharedBoardFlashIntervalId = null
        sharedBoardFlashTimeoutId = null
        sharedBoardFlashingPositions = emptySet()
        sharedBoardFlashVisible = false
        renderBoardFromSharedState()
        console.log("⚡ Shared board flash finished")
    }, 2000)
    
    console.log("⚡ Flash timeout started with ID: $sharedBoardFlashTimeoutId")
}

private fun renderBoardFromSharedState() {
    val canvas = document.getElementById("game-board") as? HTMLCanvasElement ?: return
    val ctx = canvas.getContext("2d") as CanvasRenderingContext2D
    val cellSize = canvas.width / 10.0
    
    // Dark board background
    ctx.fillStyle = BOARD_BACKGROUND
    ctx.fillRect(0.0, 0.0, canvas.width.toDouble(), canvas.height.toDouble())
    
    // Draw grid lines FIRST (behind holes)
    drawGridLines(ctx, cellSize, canvas.width.toDouble(), canvas.height.toDouble())
    
    val boardPins = InternetGameManager.getBoardPins()
    
    // Draw all cells
    for (row in 0 until 10) {
        for (col in 0 until 10) {
            val posKey = "${row}_${col}"
            val sharedPin = boardPins[posKey]
            
            // Convert SharedPin to Pin for drawing (matches Android's pattern exactly)
            val pin = if (sharedPin != null) {
                val color = try { PinColor.valueOf(sharedPin.color) } catch (_: Exception) { PinColor.RED }
                when {
                    sharedPin.isJoker -> {
                        // Match Android: Pin.joker(origColor, color)
                        // - sharedPin.color = the display/scoring color (what joker looks like)
                        // - sharedPin.originalColor = the original color (for scoring reference)
                        val origColor = if (sharedPin.originalColor != null) {
                            try { PinColor.valueOf(sharedPin.originalColor) } catch (_: Exception) { color }
                        } else color
                        val joker = Pin.joker(origColor, color)
                        if (sharedPin.isReversed) joker.reversed() else joker
                    }
                    sharedPin.isDress -> {
                        val dress = Pin.dress(color)
                        if (sharedPin.isReversed) dress.reversed() else dress
                    }
                    else -> {
                        val regular = Pin.regular(color)
                        if (sharedPin.isReversed) regular.reversed() else regular
                    }
                }
            } else null
            
            // Check if this position should flash (scoring position during flash animation)
            val isFlashing = sharedBoardFlashingPositions.contains(posKey) && sharedBoardFlashVisible
            
            drawCellForSharedBoard(ctx, row, col, pin, cellSize, isFlashing)
        }
    }
}

private fun renderScoresFromSharedState() {
    // Header scores removed in favor of below-board player scorecards
    renderPlayerScoresPanelFromSharedState()
}

private fun renderScores() {
    val state = gameEngine?.getCurrentGameState() ?: return
    renderPlayerScoresPanel(state)
}

private fun renderPlayerScoresPanel(state: GameState) {
    val panel = document.getElementById("player-scores-panel") ?: return
    val players = state.players
    
    val html = StringBuilder()
    
    // Pin and Hand score display - show based on settings
    val showPinScore = SettingsManager.isShowCurrentPinScoreEnabled()
    val showHandScore = SettingsManager.isShowCurrentHandScoreEnabled()
    
    if (showPinScore || showHandScore) {
        html.append("<div class=\"current-scores\">")
        if (showPinScore) {
            // Use 0 for pin score if this is a fresh turn, otherwise use the actual score
            val pinScore = lastPinScore ?: 0
            val pinDisplay = if (pinScore > 0) "+$pinScore" else "0"
            html.append("<span class=\"pin-score\">Pin: $pinDisplay</span>")
        }
        if (showHandScore) {
            val playerId = lastPinScorePlayerId ?: state.currentPlayerId
            val handScore = playerId?.let { playerHandScores[it] } ?: 0
            val handDisplay = handScore.toString()
            html.append("<span class=\"hand-score\">Hand: $handDisplay</span>")
        }
        html.append("</div>")
    }
    
    html.append("<div class=\"player-scores-grid\">")
    
    for (player in players) {
        val isCurrentPlayer = player.id == currentPlayerId
        val isCurrentTurn = player.id == gameCurrentPlayerId
        val cardClass = buildString {
            append("player-score-card")
            if (isCurrentPlayer) append(" current-player")
            if (isCurrentTurn) append(" active-turn")
        }
        
        html.append("<div class=\"$cardClass\">")
        if (isCurrentTurn) {
            html.append("<span class=\"turn-arrow\">▶</span>")
        }
        html.append("<span class=\"player-name\">${player.name}${if (isCurrentPlayer) " (You)" else ""}</span>")
        html.append("<span class=\"player-score\">${player.score}</span>")
        
        // Add joker count to player card
        val jokersRemaining = 4 - player.jokersUsed
        html.append("<div class=\"player-jokers\">")
        for (i in 0 until 4) {
            val status = if (i < jokersRemaining) "available" else "used"
            html.append("<span class=\"joker-dot $status\"></span>")
        }
        html.append("</div>")
        
        html.append("</div>")
    }
    
    html.append("</div>")
    panel.innerHTML = html.toString()
}

private fun renderPlayerScoresPanelFromSharedState() {
    val panel = document.getElementById("player-scores-panel") ?: return
    val players = InternetGameManager.getPlayers()
    val meta = InternetGameManager.getGameMeta()
    val rawPinScore = InternetGameManager.getLastPinScore()
    val handScore = InternetGameManager.getCurrentHandScore()
    val isMyTurn = InternetGameManager.isMyTurn()
    
    // Use 0 for pin score if this is a fresh turn (no pins placed yet), otherwise use the actual score
    val pinScore = if (isNewTurn) 0 else rawPinScore
    
    // Get settings for score display
    val showPinScore = SettingsManager.isShowCurrentPinScoreEnabled()
    val showHandScore = SettingsManager.isShowCurrentHandScoreEnabled()
    
    val html = StringBuilder()
    
    // Pin and Hand score display - show based on settings
    // Only display if at least one setting is enabled
    if (showPinScore || showHandScore) {
        html.append("<div class=\"current-scores\">")
        if (showPinScore) {
            val pinDisplay = if (pinScore > 0) "+$pinScore" else "0"
            html.append("<span class=\"pin-score\">Pin: $pinDisplay</span>")
        }
        if (showHandScore) {
            val handDisplay = handScore.toString()
            html.append("<span class=\"hand-score\">Hand: $handDisplay</span>")
        }
        html.append("</div>")
    }
    
    // Add class for 3 players to handle grid layout
    val playerCount = players.size
    val gridClass = if (playerCount == 3) "player-scores-grid three-players" else "player-scores-grid"
    html.append("<div class=\"$gridClass\">")
    
    // Sort players: Host first, then alphabetical by ID (Matches turn logic)
    val sortedPlayers = players.entries.sortedWith(
        compareByDescending<Map.Entry<String, SharedBoardManager.SharedPlayer>> { it.value.isHost }
        .thenBy { it.key }
    )
    
    for ((playerId, player) in sortedPlayers) {
        val isCurrentPlayer = playerId == currentPlayerId
        val isCurrentTurn = playerId == meta?.currentPlayerId
        val cardClass = buildString {
            append("player-score-card")
            if (isCurrentPlayer) append(" current-player")
            if (isCurrentTurn) append(" active-turn")
        }
        
        html.append("<div class=\"$cardClass\">")
        if (isCurrentTurn) {
            html.append("<span class=\"turn-arrow\">▶</span>")
        }
        html.append("<span class=\"player-name\">${if (player.isAI) "🤖 " else ""}${player.name}${if (isCurrentPlayer) " (You)" else ""}</span>")
        html.append("<span class=\"player-score\">${player.score}</span>")
        
        // Add joker count to player card
        val jokersRemaining = 4 - player.jokersUsed
        html.append("<div class=\"player-jokers\">")
        for (i in 0 until 4) {
            val status = if (i < jokersRemaining) "available" else "used"
            html.append("<span class=\"joker-dot $status\"></span>")
        }
        html.append("</div>")
        
        html.append("</div>")
    }
    
    html.append("</div>")
    panel.innerHTML = html.toString()
}

private fun renderHandFromSharedState() {
    val container = document.getElementById("hand-pins") ?: return
    val handContainer = document.getElementById("player-hand") ?: return
    val jokerStatusEl = document.getElementById("joker-status")
    
    val isMyTurn = InternetGameManager.isMyTurn()
    val isPaused = InternetGameManager.isPaused()
    val meta = InternetGameManager.getGameMeta()
    val players = InternetGameManager.getPlayers()
    
    // Get whose turn it is - everyone sees the current player's hand
    val currentTurnPlayerId = meta?.currentPlayerId
    val currentTurnPlayer = players[currentTurnPlayerId]
    
    // Update joker status for the current turn player
    // Joker status removed - now moved to player scorecard dots
    
    // Debug: Log all player hands
    console.log("renderHandFromSharedState: DEBUG - All players:")
    players.entries.forEach { (id, player) ->
        console.log("  Player $id (${player.name}): hand=${player.hand}, isHost=${player.isHost}, jokersUsed=${player.jokersUsed}")
    }
    console.log("renderHandFromSharedState: DEBUG - currentTurnPlayerId=$currentTurnPlayerId")
    
    // If paused (hand completion), show "Hand Completed"
    if (isPaused) {
        container.innerHTML = "<div style=\"color: #aaa; padding: 10px;\">Hand Completed</div>"
        return
    }
    
    // Update hand container class based on turn
    if (isMyTurn) {
        handContainer.classList.remove("not-your-turn")
    } else {
        handContainer.classList.add("not-your-turn")
    }
    
    // Update hand title to show whose hand it is
    val handTitleEl = document.querySelector("#player-hand .hand-title") as? HTMLElement
    if (handTitleEl != null) {
        handTitleEl.textContent = if (isMyTurn) "Your Hand" else "${if (currentTurnPlayer?.isAI == true) "🤖 " else ""}${currentTurnPlayer?.name ?: "Opponent"}'s Hand"
    }
    
    // Get the current player's hand (whoever's turn it is)
    val hand = InternetGameManager.getCurrentHand()
    val selectedIndex = InternetGameManager.getSelectedPinIndex()
    
    console.log("renderHandFromSharedState: currentPlayer=${currentTurnPlayer?.name}, hand=$hand, selectedIndex=$selectedIndex, isMyTurn=$isMyTurn")
    
    // If hand is empty, check if we should show "Hand Completed" or just empty
    if (hand.isEmpty()) {
        console.log("renderHandFromSharedState: ⚠️ Hand is EMPTY! currentTurnPlayerId=$currentTurnPlayerId, isMyTurn=$isMyTurn, isPaused=$isPaused")
        console.log("renderHandFromSharedState: ⚠️ currentTurnPlayer from map: ${currentTurnPlayer?.hand}")
        
        if (isMyTurn && !isPaused) {
            // It's my turn but hand is empty - this shouldn't happen unless waiting for new hand
            container.innerHTML = "<div style=\"color: #aaa; padding: 10px;\">Dealing...</div>"
        } else if (!isMyTurn) {
            // Not my turn - show whose turn it is with their hand status
            val turnPlayerHand = currentTurnPlayer?.hand ?: emptyList()
            if (turnPlayerHand.isEmpty()) {
                container.innerHTML = "<div style=\"color: #aaa; padding: 10px;\">${if (currentTurnPlayer?.isAI == true) "🤖 " else ""}${currentTurnPlayer?.name ?: "Opponent"}'s turn - Waiting for hand...</div>"
            } else {
                // This shouldn't happen - getCurrentHand should have returned the hand
                console.error("renderHandFromSharedState: BUG - currentTurnPlayer has hand but getCurrentHand returned empty!")
                container.innerHTML = "<div style=\"color: #aaa; padding: 10px;\">Syncing...</div>"
            }
        } else {
            container.innerHTML = "<div style=\"color: #aaa; padding: 10px;\">No pins in hand</div>"
        }
        return
    }
    
    val html = StringBuilder()
    hand.forEachIndexed { index, colorName ->
        val color = try { PinColor.valueOf(colorName) } catch (_: Exception) { PinColor.RED }
        val fillColor = PIN_COLORS[color] ?: "#666"
        val isSelected = index == selectedIndex
        val selectedClass = if (isSelected) " selected" else ""
        val disabledClass = if (!isMyTurn) " disabled" else ""
        
        html.append("<div class=\"hand-pin-container\">")
        html.append("<div class=\"hand-pin$selectedClass$disabledClass\" onclick=\"selectPinFromSharedHand($index)\" style=\"background-color: $fillColor;\"></div>")
        // Add JOKER button under each pin (only when it's your turn)
        if (isMyTurn) {
            html.append("<button class=\"joker-btn\" onclick=\"sharedBoardSacrificePinForJoker($index)\">JOKER</button>")
        }
        html.append("</div>")
    }
    container.innerHTML = html.toString()
}

private fun updateTurnIndicatorFromSharedState() {
    val indicator = document.getElementById("game-turn-indicator") ?: return
    val meta = InternetGameManager.getGameMeta() ?: return
    val players = InternetGameManager.getPlayers()
    
    val currentTurnPlayerId = meta.currentPlayerId
    val currentTurnPlayer = players[currentTurnPlayerId]
    val isMyTurn = currentTurnPlayerId == currentPlayerId
    
    val turnText = when {
        meta.gamePhase == "FINISHED" -> "Game Over"
        meta.gamePhase == "PAUSED" -> "Hand Complete - Undo?"
        isMyTurn -> "Your Turn"
        currentTurnPlayer != null -> "${if (currentTurnPlayer.isAI) "🤖 " else ""}${currentTurnPlayer.name}'s Turn"
        else -> "Waiting..."
    }
    
    indicator.innerHTML = turnText
    
    if (isMyTurn && meta.gamePhase == "PLAYING") {
        indicator.classList.add("my-turn")
        indicator.classList.remove("game-over-indicator")
    } else {
        indicator.classList.remove("my-turn")
        if (meta.gamePhase == "FINISHED") {
            indicator.classList.add("game-over-indicator")
        }
    }
}

private fun updateUndoButtonFromSharedState() {
    val undoBtn = document.getElementById("btn-undo") as? HTMLButtonElement ?: return
    val creditsDisplayEl = document.getElementById("undo-credits-display") as? HTMLElement
    val watchAdBtn = document.getElementById("btn-watch-ad") as? HTMLButtonElement
    
    val currentCredits = RewardedAdManager.getUndoCredits()
    
    // Get current turn state to determine if buttons should be visible
    val turnState = TurnBasedUIManager.getCurrentTurnState()
    val shouldShowButtons = turnState?.shouldShowAdButtons ?: false
    
    console.log("🔍 UNDO DEBUG: shouldShowButtons=$shouldShowButtons")
    console.log("🔍 UNDO DEBUG: turnState=$turnState")
    console.log("🔍 UNDO DEBUG: currentCredits=$currentCredits")
    
    if (shouldShowButtons) {
        // Show the watch ad button and credits display if it's a human player's turn
        watchAdBtn?.style?.display = if (RewardedAdManager.canEarnMoreCredits()) "inline-flex" else "none"
        creditsDisplayEl?.style?.display = "inline-flex"
        creditsDisplayEl?.textContent = currentCredits.toString()
        undoBtn.style.display = "block"
        
        console.log("🔍 UNDO DEBUG: Showing buttons - watchAd display=${watchAdBtn?.style?.display}, canEarnMore=${RewardedAdManager.canEarnMoreCredits()}")
        
        // Check if undo is available in shared board mode
        val canUndoMove = InternetGameManager.canUndo()
        
        console.log("🔍 SHARED UNDO DEBUG: canUndoMove=$canUndoMove")
        
        // Enable/disable the undo button based on game state
        undoBtn.disabled = !canUndoMove
        
        // Add active class when undo is available - makes button green
        if (canUndoMove) {
            undoBtn.classList.add("active")
            console.log("🟢 Undo button set to ACTIVE (green)")
        } else {
            undoBtn.classList.remove("active")
            console.log("⚪ Undo button set to inactive")
        }
    } else {
        // Hide buttons during AI turns or when it's not the player's turn
        console.log("🔍 Hiding ad buttons - AI turn or not player's turn")
        watchAdBtn?.style?.display = "none"
        creditsDisplayEl?.style?.display = "none"
        undoBtn.style.display = "none"
    }
}

private fun displayGameOverFromSharedState(scores: Map<String, Int>, winnerId: String?) {
    val dialog = document.getElementById("game-over-dialog") ?: return
    val winnerDisplay = document.getElementById("game-over-winner")
    val scoresList = document.getElementById("game-over-scores")
    val players = InternetGameManager.getPlayers()
    
    val winnerName = if (winnerId != null) players[winnerId]?.name ?: "Unknown" else "Tie"
    
    // Submit score to leaderboard
    val myPlayerId = currentPlayerId
    val myScore = scores[myPlayerId]
    val myName = currentPlayerName ?: "Unknown"
    val isWin = winnerId == myPlayerId
    
    if (myPlayerId != null && myScore != null) {
        console.log("📊 Submitting score to leaderboard: player=$myPlayerId, name=$myName, score=$myScore, win=$isWin")
        FirebaseRoomManager.submitScore(myPlayerId, myName, myScore, isWin) { success ->
            if (success) {
                console.log("✅ Score submitted to leaderboard successfully")
            } else {
                console.log("⚠️ Failed to submit score to leaderboard")
            }
        }
    }
    
    // Winner display
    if (winnerDisplay != null) {
        val isWinner = winnerId == currentPlayerId
        val winnerHtml = StringBuilder()
        if (winnerId != null) {
            winnerHtml.append("<span class=\"winner-label\">Winner</span>")
            winnerHtml.append("<span class=\"winner-name\">${if (isWinner) "🎉 YOU WIN 🎉" else winnerName}</span>")
        } else {
            winnerHtml.append("<span class=\"winner-name\">TIE GAME</span>")
        }
        winnerDisplay.innerHTML = winnerHtml.toString()
    }
    
    // Scores list
    if (scoresList != null) {
        val scoresHtml = StringBuilder()
        for ((playerId, score) in scores.entries.sortedByDescending { it.value }) {
            val playerName = players[playerId]?.name ?: playerId
            val isMe = playerId == currentPlayerId
            scoresHtml.append("<div class=\"final-score-row ${if (isMe) "is-me" else ""}\">")
            scoresHtml.append("<span class=\"player-name\">$playerName</span>")
            scoresHtml.append("<span class=\"player-score\">$score</span>")
            scoresHtml.append("</div>")
        }
        scoresList.innerHTML = scoresHtml.toString()
    }
    
    dialog.asDynamic().style.display = "flex"
}

private fun setupBoardClickHandlerForSharedBoard() {
    val canvas = document.getElementById("game-board") as? HTMLCanvasElement ?: return
    
    canvas.onclick = { event: dynamic ->
        // Initialize sound on first user interaction (required for mobile browsers)
        SoundManager.initialize()
        
        val mouseEvent = event as MouseEvent
        val rect = canvas.getBoundingClientRect()
        val x = mouseEvent.clientX - rect.left
        val y = mouseEvent.clientY - rect.top
        val cellSize = canvas.width / 10.0
        val col = (x / cellSize).toInt()
        val row = (y / cellSize).toInt()
        
        if (row in 0..9 && col in 0..9) {
            console.log("Board click at ($row, $col)")
            // Proactively unlock audio on board click (valid user gesture)
            SoundManager.unlockAudio()
            
            // Check if we have a pending joker to place
            if (sharedBoardPendingJoker != null) {
                InternetGameManager.makeMoveWithJoker(row, col, sharedBoardPendingJoker!!)
                sharedBoardPendingJoker = null
                sharedBoardJokerSacrificeIndex = null
            } else {
                InternetGameManager.makeMove(row, col)
            }
        }
    }
    
    // Enable cursor
    canvas.style.cursor = "pointer"
}

// Shared board joker state
private var sharedBoardJokerSacrificeIndex: Int? = null
private var sharedBoardPendingJoker: String? = null  // The color the joker will become

// JS-callable function for pin selection in shared board mode
@JsExport
@JsName("selectPinFromSharedHand")
fun selectPinFromSharedHand(index: Int) {
    console.log("selectPinFromSharedHand: index=$index")
    // Proactively unlock audio on pin selection (valid user gesture)
    SoundManager.unlockAudio()
    
    // Cancel any pending joker selection
    sharedBoardPendingJoker = null
    sharedBoardJokerSacrificeIndex = null
    InternetGameManager.selectPin(index)
}

// JS-callable function for joker creation in shared board mode
@JsExport
@JsName("sharedBoardSacrificePinForJoker")
fun sharedBoardSacrificePinForJoker(index: Int) {
    console.log("sharedBoardSacrificePinForJoker: index=$index")
    // Proactively unlock audio on joker sacrifice (valid user gesture)
    SoundManager.unlockAudio()
    
    if (!InternetGameManager.isMyTurn()) {
        console.log("Not your turn")
        return
    }
    
    // Check if player can still use jokers
    if (!InternetGameManager.canUseJoker()) {
        console.log("Maximum jokers used (4 per game)")
        window.alert("You have already used the maximum number of jokers (4) this game!")
        return
    }
    
    val hand = InternetGameManager.getCurrentHand()
    if (index !in hand.indices) {
        console.log("Invalid index")
        return
    }
    
    sharedBoardJokerSacrificeIndex = index
    
    // Show the joker color picker dialog
    val dialog = document.getElementById("joker-dialog") as? HTMLElement ?: return
    dialog.style.display = "flex"
    
    // Set up color button click handlers for shared board joker selection
    val buttons = document.querySelectorAll(".joker-color-btn")
    for (i in 0 until buttons.length) {
        val btn = buttons.item(i) as? HTMLElement ?: continue
        val color = btn.getAttribute("data-color") ?: continue
        btn.onclick = { sharedBoardSelectJokerColor(color) }
    }
}

// Called when user selects a joker color in shared board mode
fun sharedBoardSelectJokerColor(colorName: String) {
    console.log("sharedBoardSelectJokerColor: $colorName")
    
    val sacrificeIndex = sharedBoardJokerSacrificeIndex
    if (sacrificeIndex == null) {
        console.log("No sacrifice index set")
        hideJokerDialog()
        return
    }
    
    // Set the pending joker color - the next board click will place it
    sharedBoardPendingJoker = colorName
    
    // Select the sacrificed pin so it's used for the joker
    InternetGameManager.selectPin(sacrificeIndex)
    
    hideJokerDialog()
    
    // Update hand to show joker is pending
    renderHandFromSharedState()
    
    console.log("Joker ready to place as $colorName. Click on board to place.")
}

// ========================================
// END SHARED BOARD RENDERING FUNCTIONS
// ========================================


// Hand display rendering
private var pinToSacrificeIndex: Int? = null  // Track which pin is being sacrificed for joker

private fun renderHand() {
    val container = document.getElementById("hand-pins") ?: return
    val handContainer = document.getElementById("player-hand") ?: return
    
    // If waiting for turn switch (hand completion pause), hide hand
    if (isInHandCompletionPause || pendingCurrentPlayerId != null) {
        container.innerHTML = "<div style=\"color: #aaa; padding: 10px;\">Hand Completed</div>"
        return
    }
    
    val state = gameEngine?.getCurrentGameState() ?: return
    
    // CRITICAL FIX: Use GameEngine's currentPlayerId directly (like Android does)
    // Don't rely on the separate gameCurrentPlayerId variable which can get out of sync
    val engineCurrentPlayerId = state.currentPlayerId ?: state.players.getOrNull(state.currentPlayerIndex)?.id
    val isMyTurn = engineCurrentPlayerId == currentPlayerId
    
    // Update hand container class based on turn
    if (isMyTurn) {
        handContainer.classList.remove("not-your-turn")
    } else {
        handContainer.classList.add("not-your-turn")
    }
    
    val availablePins = state.getAvailablePins()
    
    // DEBUG: Log the pins in hand
    console.log("renderHand: ${availablePins.size} pins in hand, isMyTurn=$isMyTurn, engineCurrentPlayerId=$engineCurrentPlayerId, myId=$currentPlayerId")
    availablePins.forEachIndexed { index, pin ->
        console.log("  Pin $index: ${pin.color}, isJoker=${pin.isJoker}, originalColor=${pin.originalColor}")
    }
    
    if (availablePins.isEmpty()) {
        container.innerHTML = "<div style=\"color: #aaa;\">No pins available</div>"
        return
    }
    
    val html = StringBuilder()
    
    // Check if we're in joker creation mode
    // OLD INTERFACE: Show dialog instead of inline UI
    // val sacrificeIndex = pinToSacrificeIndex // Unused now
    
    // Normal pin display with joker buttons
    availablePins.forEachIndexed { index, pin ->
        val isSelected = index == selectedPinIndex
        val colorClass = if (pin.isJoker) "joker" else pin.color.name.lowercase()
        val selectedClass = if (isSelected) " selected" else ""
        val disabledClass = if (!isMyTurn) " disabled" else ""
        val label = if (pin.isJoker) "J" else ""
        
        html.append("<div class=\"hand-pin-container\">")
        html.append("<div class=\"hand-pin $colorClass$selectedClass$disabledClass\" onclick=\"selectHandPin($index)\">")
        html.append(label)
        html.append("</div>")
        // Add JOKER button under each non-joker pin
        if (!pin.isJoker && isMyTurn) {
            html.append("<button class=\"joker-btn\" onclick=\"sacrificePinForJoker($index)\">JOKER</button>")
        }
        html.append("</div>")
    }
    
    container.innerHTML = html.toString()
    
    // Update the hand header UI (joker status, credits)
    updateHandUI()
    updateUndoButton()
}

@JsExport
@JsName("sacrificePinForJoker")
fun sacrificePinForJoker(index: Int) {
    val state = gameEngine?.getCurrentGameState() ?: return
    
    // Check if player can still use jokers
    if (!state.currentPlayer.canUseJoker()) {
        window.alert("You have already used the maximum number of jokers (4) this game!")
        return
    }
    
    val availablePins = state.getAvailablePins()
    if (index in availablePins.indices && !availablePins[index].isJoker) {
        pinToSacrificeIndex = index
        renderHand()
        console.log("Sacrificing pin at index $index for joker")
        
        // OLD INTERFACE: Show dialog
        showJokerDialogForHand()
    }
}

private fun showJokerDialogForHand() {
    val dialog = document.getElementById("joker-dialog") as? HTMLElement ?: return
    dialog.style.display = "flex"
    
    // Set up color button click handlers for HAND selection
    val buttons = document.querySelectorAll(".joker-color-btn")
    for (i in 0 until buttons.length) {
        val btn = buttons.item(i) as? HTMLElement ?: continue
        val color = btn.getAttribute("data-color") ?: continue
        btn.onclick = { selectJokerColorFromHand(color) }
    }
    
    // Update cancel button to call cancelJokerFromHand
    val cancelBtn = dialog.querySelector(".btn-secondary") as? HTMLElement
    cancelBtn?.onclick = { cancelJokerFromHand() }
}

@JsExport
@JsName("selectJokerColorFromHand")
fun selectJokerColorFromHand(colorName: String) {
    val sacrificeIndex = pinToSacrificeIndex ?: return
    val state = gameEngine?.getCurrentGameState() ?: return
    val availablePins = state.getAvailablePins()
    if (sacrificeIndex !in availablePins.indices) return
    
    val sacrificedPin = availablePins[sacrificeIndex]
    val jokerColor = try {
        PinColor.valueOf(colorName)
    } catch (e: Exception) {
        console.error("Invalid joker color: $colorName")
        return
    }
    
    // Hide dialog immediately
    hideJokerDialog()
    
    // Create joker pin - store the original color and the played-as color
    val jokerPin = Pin.joker(originalColor = sacrificedPin.color, playedAsColor = jokerColor)
    
    // Set this as the selected pin for placement
    selectedPinIndex = sacrificeIndex
    pinToSacrificeIndex = null
    
    // Store the joker pin for when user clicks on board
    pendingJokerPin = jokerPin
    
    renderHand()
    console.log("Created joker: ${sacrificedPin.color} -> $colorName. Click on board to place.")
}

@JsExport
@JsName("cancelJokerFromHand")
fun cancelJokerFromHand() {
    pinToSacrificeIndex = null
    pendingJokerPin = null
    hideJokerDialog()
    renderHand()
}

@JsExport
@JsName("selectPin")
fun selectPin(index: Int) {
    val state = gameEngine?.getCurrentGameState() ?: return
    val availablePins = state.getAvailablePins()
    if (index in availablePins.indices) {
        selectedPinIndex = index
        renderHand()
        console.log("Selected pin at index $index: ${availablePins[index].color}")
    }
}

// Joker color picker dialog
private fun showJokerDialog(pin: Pin, position: Position) {
    pendingJokerPin = pin
    pendingJokerPosition = position
    val dialog = document.getElementById("joker-dialog") as? HTMLElement ?: return
    dialog.style.display = "flex"
    
    // Set up color button click handlers
    val buttons = document.querySelectorAll(".joker-color-btn")
    for (i in 0 until buttons.length) {
        val btn = buttons.item(i) as? HTMLElement ?: continue
        val color = btn.getAttribute("data-color") ?: continue
        btn.onclick = { selectJokerColor(color) }
    }
}

private fun hideJokerDialog() {
    val dialog = document.getElementById("joker-dialog") as? HTMLElement ?: return
    dialog.style.display = "none"
    pendingJokerPin = null
    pendingJokerPosition = null
}

@JsExport
@JsName("selectJokerColor")
fun selectJokerColor(colorName: String) {
    // Proactively unlock audio on joker color selection (valid user gesture)
    SoundManager.unlockAudio()
    
    // Use shared board joker handling when enabled
    if (USE_SHARED_BOARD) {
        sharedBoardSelectJokerColor(colorName)
        return
    }
    
    val pin = pendingJokerPin ?: return
    val position = pendingJokerPosition ?: return
    val playerId = currentPlayerId ?: return
    val roomId = currentRoomId ?: return
    
    val jokerColor = try {
        PinColor.valueOf(colorName)
    } catch (e: Exception) {
        console.error("Invalid joker color: $colorName")
        return
    }
    
    // Create joker pin with chosen color - use the original color and played-as color
    val jokerWithColor = Pin.joker(originalColor = pin.color, playedAsColor = jokerColor)
    
    console.log("Placing joker as $colorName at $position")
    hideJokerDialog()
    
    // Calculate score before applying move
    val state = gameEngine?.getCurrentGameState() ?: return
    val move = Move(playerId, jokerWithColor, position)
    
    // CRITICAL: Place pin on temp board FIRST, then calculate score (like Android's calculateScore)
    // This is essential for detecting 6+ pin lines that include the newly placed pin
    val tempBoard = state.board.placePinAt(position, jokerWithColor)
    val scoreResult = ScoreCalculator().calculateMoveScore(tempBoard, move)
    
    // Update score display and play sounds (like regular moves)
    updateScoreDisplay(scoreResult.points, playerId)
    SoundManager.playPinPlaced()
    SoundManager.playScoreSound(scoreResult.points)
    if (scoreResult.pinsToReverse.isNotEmpty()) {
        SoundManager.playPinsReversed()
    }
    
    // Trigger score flash for newly placed pin + patterns formed + reversed pins
    val flashPositions = mutableListOf(position)
    scoreResult.linesFormed.forEach { pattern -> flashPositions.addAll(pattern) }
    flashPositions.addAll(scoreResult.pinsToReverse)
    triggerScoreFlash(flashPositions.distinct())
    
    // Capture original pin states BEFORE applying move (for undo)
    // CRITICAL: Like Android, we need to capture:
    // 1. Pins that will be reversed (line-of-6)
    // 2. ALL jokers on the board if this move will complete the hand (because turnJokersBlack will affect them)
    val willCompleteHand = state.currentHand.size == 1
    val positionsToCapture = scoreResult.pinsToReverse.toMutableSet()
    
    console.log("🔄 UNDO CAPTURE (selectJokerColor): willCompleteHand=$willCompleteHand, pinsToReverse=${scoreResult.pinsToReverse.size}")
    
    if (willCompleteHand) {
        console.log("🔄 UNDO CAPTURE (selectJokerColor): Hand will complete - capturing all jokers on board")
        // Capture from tempBoard which has the new pin placed
        val allOccupiedPositions = tempBoard.getOccupiedPositions()
        console.log("🔄 UNDO CAPTURE (selectJokerColor): tempBoard has ${allOccupiedPositions.size} occupied positions")
        for (pos in allOccupiedPositions) {
            val existingPin = tempBoard.getPinAt(pos)
            val (r, c) = pos.toLegacy()
            if (existingPin != null && existingPin.isJoker) {
                console.log("🔄 UNDO CAPTURE (selectJokerColor): Found joker at ($r,$c): isJokerTurnedBlack=${existingPin.isJokerTurnedBlack}")
                if (!existingPin.isJokerTurnedBlack) {
                    positionsToCapture.add(pos)
                    console.log("🔄 UNDO CAPTURE (selectJokerColor): ✅ Adding joker at ($r,$c) to capture list")
                } else {
                    console.log("🔄 UNDO CAPTURE (selectJokerColor): ⚠️ Skipping joker at ($r,$c) - already turned black!")
                }
            }
        }
    }
    
    console.log("🔄 UNDO CAPTURE (selectJokerColor): Total positions to capture: ${positionsToCapture.size}")
    
    // Capture original pin states from the ORIGINAL board (before the move)
    // For the newly placed pin, capture from tempBoard
    val originalPinStates = positionsToCapture.mapNotNull { pos ->
        val (r, c) = pos.toLegacy()
        if (pos == position) {
            // This is the newly placed pin - capture from tempBoard
            val pin = tempBoard.getPinAt(pos)
            console.log("🔄 UNDO CAPTURE (selectJokerColor): Capturing NEW pin at ($r,$c) from tempBoard: ${pin?.color}, joker=${pin?.isJoker}, black=${pin?.isJokerTurnedBlack}")
            pin?.let { Pair(pos, it) }
        } else {
            // Existing pins - capture from original board
            val pin = state.board.getPinAt(pos)
            console.log("🔄 UNDO CAPTURE (selectJokerColor): Capturing EXISTING pin at ($r,$c) from state.board: ${pin?.color}, joker=${pin?.isJoker}, black=${pin?.isJokerTurnedBlack}")
            pin?.let { Pair(pos, it) }
        }
    }
    console.log("🔄 UNDO CAPTURE (selectJokerColor): Final captured ${originalPinStates.size} pins for undo")
    
    // CRITICAL: Capture hand size BEFORE applying move to detect hand completion
    val handSizeBeforeJokerMove = state.currentHand.size
    
    // Apply move locally FIRST for immediate feedback
    // Use applyLocalMove to update both board AND hand
    try {
        gameEngine?.applyLocalMove(move)
        console.log("Local joker move applied successfully")
        
        // Track move for potential undo - use the ORIGINAL pin (before joker conversion) for hand restoration
        trackMoveForUndo(move, pin, originalPinStates)
        
        // Update UI after move
        renderScores()
        renderHand()
        updateTurnIndicator()
        updateUndoButton()  // CRITICAL: Always update undo button after tracking move
    } catch (e: Exception) {
        console.error("Failed to apply local joker move: ${e.message}")
        return
    }
    
    selectedPinIndex = 0  // Reset selection
    
    // Check if hand completed - if hand size was 1 before and is now 4, hand was completed
    val stateAfterJokerMove = gameEngine?.getCurrentGameState()
    val handSizeAfterJokerMove = stateAfterJokerMove?.currentHand?.size ?: -1
    val jokerHandCompleted = handSizeBeforeJokerMove == 1 && handSizeAfterJokerMove == 4
    
    // CRITICAL: Increment hand move counter and reset on hand completion
    currentHandMoveCount++
    console.log("🔍 JOKER HAND CHECK: handSizeBefore=$handSizeBeforeJokerMove, handSizeAfter=$handSizeAfterJokerMove, handJustCompleted=$jokerHandCompleted, currentHandMoveCount=$currentHandMoveCount")
    
    // CRITICAL FIX: Set isInHandCompletionPause IMMEDIATELY before any async operations
    // This prevents the GameStateUpdate from host from clearing undo tracking
    if (jokerHandCompleted) {
        console.log("✅ Hand completed after joker move - setting pause flag IMMEDIATELY")
        isInHandCompletionPause = true  // Set flag BEFORE broadcast to prevent race condition
        // Reset hand move counter for next hand
        currentHandMoveCount = 0
    }
    
    // Then broadcast to Firebase and Pusher (this is async and host may respond quickly)
    // CRITICAL: Use currentHandMoveCount for moveNumber, not moves.size (which is reset after sync)
    val moveCount = currentHandMoveCount
    console.log("📤 Broadcasting joker move with moveNumber=$moveCount (currentHandMoveCount)")
    GameEventManager.broadcastMove(roomId, playerId, jokerWithColor, position, currentPlayerName, moveCount) { success, error ->
        if (!success) {
            console.error("Failed to broadcast joker move: $error")
        }
    }
    
    // Now start the full hand completion pause (countdown timer, UI updates, etc.)
    if (jokerHandCompleted) {
        console.log("Starting full hand completion pause after joker move")
        renderBoard()  // Re-render to show jokers turned black
        renderScores()
        renderHand()
        startHandCompletionPause()  // Start the countdown (flag already set)
        updateUndoButton()
    }
}

@JsExport
@JsName("cancelJoker")
fun cancelJoker() {
    hideJokerDialog()
}

@JsExport
@JsName("clearAllRooms")
fun clearAllRooms() {
    console.log("⚠️ DEBUG: Clearing all rooms...")
    val rooms = listOf("room-alpha", "room-beta", "room-charlie", "room-delta", "room-echo", "room-foxtrot")
    
    // Recursive function to clear rooms sequentially
    fun clearNext(index: Int) {
        if (index >= rooms.size) {
            window.alert("✅ All rooms have been cleared!")
            return
        }
        
        val roomId = rooms[index]
        FirebaseRoomManager.forceResetRoom(roomId) {
            console.log("Cleared $roomId")
            clearNext(index + 1)
        }
    }
    
    clearNext(0)
}


private fun updateTurnIndicator() {
    val indicator = document.getElementById("game-turn-indicator") ?: return
    val state = gameEngine?.getCurrentGameState()
    
    // CRITICAL FIX: Use GameEngine's currentPlayerId directly (like Android does)
    val engineCurrentPlayerId = state?.currentPlayerId ?: state?.players?.getOrNull(state?.currentPlayerIndex ?: 0)?.id ?: gameCurrentPlayerId
    val isMyTurn = engineCurrentPlayerId == currentPlayerId
    val playerName = gamePlayers[engineCurrentPlayerId]?.name ?: "?"
    
    // Show countdown during hand completion pause
    if (isInHandCompletionPause) {
        indicator.textContent = "Undo available: ${handCompletionPauseTimeRemaining}s"
        indicator.classList.add("my-turn")  // Highlight during pause
        return
    }
    
    if (isMyTurn) {
        indicator.textContent = "Your turn!"
        indicator.classList.add("my-turn")
    } else {
        indicator.textContent = "$playerName's turn"
        indicator.classList.remove("my-turn")
    }
    updateBoardInteraction()
}

private var boardClickListenerAttached = false

private fun updateBoardInteraction() {
    val canvas = document.getElementById("game-board") as? HTMLCanvasElement ?: return
    val state = gameEngine?.getCurrentGameState()
    
    // CRITICAL FIX: Use GameEngine's currentPlayerId directly (like Android does)
    val engineCurrentPlayerId = state?.currentPlayerId ?: state?.players?.getOrNull(state?.currentPlayerIndex ?: 0)?.id ?: gameCurrentPlayerId
    val isMyTurn = engineCurrentPlayerId == currentPlayerId
    
    // Block moves during hand completion pause
    val canMove = isMyTurn && !isInHandCompletionPause
    canvas.style.cursor = if (canMove) "pointer" else "not-allowed"
    
    if (!boardClickListenerAttached) {
        canvas.addEventListener("click", { event -> handleBoardClick(event as MouseEvent) })
        boardClickListenerAttached = true
    }
}

private fun handleBoardClick(event: MouseEvent) {
    // Legacy handler - not used when USE_SHARED_BOARD = true
    // setupBoardClickHandlerForSharedBoard handles clicks for shared board architecture
    return
}

// Score tracking for each player's current hand
private var playerHandScores: MutableMap<String, Int> = mutableMapOf()
private var lastPinScore: Int? = null
private var lastPinScorePlayerId: String? = null

// Update score display after placing a pin
private fun updateScoreDisplay(points: Int, playerId: String) {
    lastPinScore = points
    lastPinScorePlayerId = playerId
    
    // Update the player's hand score - accumulate, never reset here
    val currentHandScore = playerHandScores.getOrElse(playerId) { 0 }
    playerHandScores[playerId] = currentHandScore + points
    
    // Clear any existing timeout
    scoreClearTimeoutId?.let { window.clearTimeout(it) }
    
    // Auto-clear pin score after 3 seconds, but NOT if we are in a pause
    // During a pause, we want the final pin score to remain visible
    scoreClearTimeoutId = window.setTimeout({
        if (!isInHandCompletionPause) {
            lastPinScore = null
            lastPinScorePlayerId = null
            renderScores()
        }
    }, 3000)
    
    renderScores()
}

/**
 * Trigger the score flash animation for the given positions.
 * High-contrast white flashing effect matching Android.
 */
private fun triggerScoreFlash(positions: List<Position>) {
    if (positions.isEmpty()) return
    
    console.log("⚡ Triggering score flash for ${positions.size} pins")
    
    // Cancel any existing flash
    scoreFlashIntervalId?.let { window.clearInterval(it) }
    scoreFlashTimeoutId?.let { window.clearTimeout(it) }
    
    flashingPositions = positions.toSet()
    isFlashVisible = true
    renderBoard() // Initial render with flash
    
    // Toggle flash every 400ms for 2 seconds
    scoreFlashIntervalId = window.setInterval({
        isFlashVisible = !isFlashVisible
        renderBoard()
    }, 400)
    
    // End flash after 2 seconds
    scoreFlashTimeoutId = window.setTimeout({
        scoreFlashIntervalId?.let { window.clearInterval(it) }
        scoreFlashIntervalId = null
        scoreFlashTimeoutId = null
        flashingPositions = emptySet()
        isFlashVisible = false
        renderBoard()
        console.log("⚡ Score flash finished")
    }, 2000)
}

private fun resetHandScore(specificPlayerId: String? = null) {
    // GUARD: Never reset scores mid-hand (e.g. 1, 2, 3 pins placed).
    // Only reset at Start (0), End (4, 8...), or if no game state exists.
    val state = gameEngine?.getCurrentGameState()
    val moves = state?.moves?.size ?: 0
    if (moves > 0 && moves % 4 != 0) {
        logWarn("⚠️ BLOCKED resetHandScore mid-hand (moves=$moves) for player $specificPlayerId to prevent score disappearance.")
        return
    }

    val targetId = specificPlayerId ?: gameCurrentPlayerId
    if (targetId != null) {
        log("🔄 RESETTING hand score for player: $targetId")
        playerHandScores[targetId] = 0
    }
    
    // Only clear the "last pin" info if we are not in a pause
    if (!isInHandCompletionPause) {
        lastPinScore = null
        lastPinScorePlayerId = null
        scoreClearTimeoutId?.let { window.clearTimeout(it) }
    }
    renderScores()
}


// ========================================
// Undo Functionality
// ========================================

/**
 * Update the undo button visibility and state.
 * Shows the button when the player has made a move and it's still their turn.
 * 
 * Requirements: 11.1
 */
private fun updateUndoButton() {
    val undoBtn = document.getElementById("btn-undo") as? HTMLButtonElement ?: return
    val creditsDisplayEl = document.getElementById("undo-credits-display") as? HTMLElement
    val watchAdBtn = document.getElementById("btn-watch-ad") as? HTMLButtonElement
    
    // Rewarded ads enabled for Play Store version
    // Show the watch ad button and credits display
    watchAdBtn?.style?.display = "block"
    creditsDisplayEl?.style?.display = "none"
    
    // Show undo button only when:
    // 1. Player has made a move in current hand
    // 2. It's still the player's turn OR we're in hand completion pause (turn just switched)
    // 3. Game is active
    val state = gameEngine?.getCurrentGameState()
    
    // CRITICAL FIX: Use GameEngine's currentPlayerId directly (like Android does)
    val engineCurrentPlayerId = state?.currentPlayerId ?: state?.players?.getOrNull(state?.currentPlayerIndex ?: 0)?.id ?: gameCurrentPlayerId
    val isMyTurn = engineCurrentPlayerId == currentPlayerId
    val hasMadeMove = lastLocalMove != null && canUndoCurrentMove
    val gameActive = gameEngine != null
    
    // CRITICAL FIX: During hand completion pause, allow undo even if turn has switched
    // The player who just completed their hand should be able to undo their last move
    val canUndoDuringPause = isInHandCompletionPause && hasMadeMove
    
    // Enable/disable undo button based on state (no credit check - unlimited undos)
    val canUndo = gameActive && (isMyTurn || canUndoDuringPause) && hasMadeMove
    
    console.log("🔍 UNDO DEBUG: isMyTurn=$isMyTurn, hasMadeMove=$hasMadeMove, isInPause=$isInHandCompletionPause, canUndoDuringPause=$canUndoDuringPause, canUndo=$canUndo")
    console.log("🔍 UNDO DEBUG: lastLocalMove=${lastLocalMove != null}, canUndoCurrentMove=$canUndoCurrentMove")
    
    undoBtn.disabled = !canUndo
    
    // Add active class when undo is available - makes button more visible with green background
    if (canUndo) {
        undoBtn.classList.add("active")
    } else {
        undoBtn.classList.remove("active")
    }
}

/**
 * Watch a rewarded ad to earn an undo credit.
 */
@JsExport
@JsName("watchAdForCredit")
fun watchAdForCredit() {
    console.log("=== watchAdForCredit ===")
    
    if (!RewardedAdManager.canEarnMoreCredits()) {
        console.log("Already at max credits")
        window.alert("You already have the maximum number of undo credits (10)!")
        return
    }
    
    RewardedAdManager.showRewardedAd(
        onRewardEarned = {
            console.log("Credit earned from ad!")
            updateUndoButton()
            updateHandUI()
        },
        onAdClosed = {
            console.log("Ad closed")
        },
        onAdFailed = {
            console.log("Ad failed to show")
            window.alert("Could not load ad. Please try again later.")
        }
    )
}

/**
 * Update the hand UI (joker status, credits display).
 */
private fun updateHandUI() {
    val jokerStatusEl = document.getElementById("joker-status")
    val creditsDisplayEl = document.getElementById("undo-credits-display")
    val watchAdBtn = document.getElementById("btn-watch-ad") as? HTMLButtonElement
    
    // Update joker status
    val state = gameEngine?.getCurrentGameState()
    val currentPlayer = state?.players?.find { it.id == currentPlayerId }
    if (currentPlayer != null && jokerStatusEl != null) {
        val jokersRemaining = 4 - currentPlayer.jokersUsed
        jokerStatusEl.textContent = "Jokers: $jokersRemaining/4"
    }
    
    // Update credits display
    val credits = RewardedAdManager.getUndoCredits()
    creditsDisplayEl?.textContent = credits.toString()
    
    // Update watch ad button state
    if (watchAdBtn != null) {
        watchAdBtn.disabled = credits >= 10
    }
}

/**
 * Request to undo the last move.
 * If player has credits, use them. Otherwise, show rewarded ad.
 * 
 * Requirements: 11.1, 11.6
 */
@JsExport
@JsName("requestUndo")
fun requestUndo() {
    console.log("=== requestUndo ===")
    // Proactively unlock audio on undo request (valid user gesture)
    SoundManager.unlockAudio()
    
    // Check if using shared board architecture
    if (USE_SHARED_BOARD) {
        requestUndoSharedBoard()
        return
    }
    
    if (!canUndoCurrentMove || lastLocalMove == null) {
        console.log("Cannot undo - no move to undo")
        return
    }
    
    val roomId = currentRoomId
    val playerId = currentPlayerId
    
    if (roomId == null || playerId == null) {
        console.log("Cannot undo - not in a room")
        return
    }
    
    // Cancel hand completion pause if active (undo means hand is no longer complete)
    if (isInHandCompletionPause) {
        console.log("Cancelling hand completion pause due to undo")
        cancelHandCompletionPause()
    }
    
    // Check if player has undo credits
    if (RewardedAdManager.hasUndoCredits()) {
        console.log("Player has credits - using one to undo")
        performUndo(roomId, playerId)
    } else {
        console.log("No credits - showing rewarded ad")
        // Show rewarded ad to earn credit
        RewardedAdManager.showRewardedAd(
            onRewardEarned = {
                console.log("Reward earned! Now performing undo")
                performUndo(roomId, playerId)
            },
            onAdClosed = {
                console.log("Ad closed")
                updateUndoButton()
            },
            onAdFailed = {
                console.log("Ad failed to show")
                window.alert("Could not load ad. Please try again later.")
            }
        )
    }
}

/**
 * Request undo for shared board mode.
 */
private fun requestUndoSharedBoard() {
    console.log("=== requestUndoSharedBoard ===")
    
    if (!InternetGameManager.canUndo()) {
        console.log("Cannot undo in shared board mode")
        return
    }
    
    // Credits disabled - just perform undo directly
    console.log("Performing undo (credits disabled)")
    performUndoSharedBoard()
}

/**
 * Perform the actual undo operation for shared board mode.
 */
private fun performUndoSharedBoard() {
    console.log("performUndoSharedBoard: Starting...")
    
    // Credits disabled - just perform the undo directly
    console.log("performUndoSharedBoard: Calling InternetGameManager.undoLastMove()")
    
    val success = InternetGameManager.undoLastMove()
    console.log("performUndoSharedBoard: undoLastMove returned $success")
    if (success) {
        console.log("✅ Undo successful in shared board mode")
        updateUndoButtonFromSharedState()
    } else {
        console.error("❌ Undo failed in shared board mode")
    }
}


/**
 * Perform the actual undo operation.
 * Uses a credit and broadcasts the undo event.
 */
private fun performUndo(roomId: String, playerId: String) {
    // Use the credit
    if (!RewardedAdManager.consumeUndoReward()) {
        console.log("Failed to consume undo credit")
        return
    }
    
    console.log("Undo credit consumed, broadcasting undo event")
    
    // Get the position of the move being undone
    val movePosition = lastLocalMove?.position
    
    // Broadcast undo event to Firebase with position
    GameEventManager.broadcastUndoMove(
        roomId = roomId, 
        playerId = playerId,
        position = movePosition
    ) { success, error ->
        if (success) {
            console.log("✅ Undo event broadcast successfully")
            // Apply undo locally
            applyUndoLocally()
            
            // CRITICAL: Always broadcast GameStateUpdate in Android-compatible format after undo
            console.log("Broadcasting Android-compatible board state after undo")
            broadcastBoardStateAfterUndo(roomId, playerId)
        } else {
            console.error("❌ Failed to broadcast undo: $error")
        }
    }
}

/**
 * Broadcast board state after undo to sync un-reversed pins to other devices.
 * Uses Android-compatible format with full GameState including turn information.
 * CRITICAL: Both host AND client broadcast GameStateUpdate after undo.
 * The player who performed the undo has the authoritative state.
 */
private fun broadcastBoardStateAfterUndo(roomId: String, playerId: String) {
    val engine = gameEngine ?: return
    val state = engine.getCurrentGameState() ?: return
    
    val role = if (isHost) "HOST" else "CLIENT"
    console.log("🔄 $role: Broadcasting board state after undo - ${state.board.getOccupiedPositions().size} pins")
    console.log("Current player: ${state.currentPlayer.name} (${state.currentPlayer.id}), index: ${state.currentPlayerIndex}")
    console.log("Current hand size: ${state.currentHand.size}")
    
    // CRITICAL: Use Android-compatible format with full GameState
    // This ensures Android host can properly synchronize the board state, turn, and hand
    // The player who performed the undo has the authoritative state
    GameEventManager.broadcastGameStateUpdateForAndroid(
        roomId = roomId,
        playerId = playerId,
        board = state.board,
        players = state.players,
        currentPlayerIndex = state.currentPlayerIndex,
        currentHand = state.currentHand,
        playerHands = state.playerHands,
        handCount = state.handCount
    ) { success, error ->
        if (success) {
            console.log("✅ $role: Android-compatible board state broadcast after undo successful")
        } else {
            console.error("❌ $role: Failed to broadcast board state after undo: $error")
        }
    }
}

/**
 * Apply undo locally - remove the last move from the board and restore pin to hand.
 * Also restores any pins that were reversed by the move (line-of-6) to their original state.
 * This matches Android's approach of storing and restoring complete pin states.
 * 
 * Requirements: 11.5
 */
private fun applyUndoLocally() {
    val move = lastLocalMove ?: return
    
    console.log("🔄🔄🔄 UNDO: Starting applyUndoLocally for move at ${move.position} 🔄🔄🔄")
    
    val engine = gameEngine ?: return
    val state = engine.getCurrentGameState() ?: return
    
    // CRITICAL: Detect if this was a hand-completing move (4th pin)
    // If the current hand has 4 pins and the turn has switched, this was a hand-completing move
    // We need to restore to a hand with just 1 pin, not add to the new 4-pin hand
    val wasHandCompletingMove = state.currentHand.size == 4 && state.currentPlayerId != move.playerId
    console.log("UNDO: wasHandCompletingMove=$wasHandCompletingMove (currentHand.size=${state.currentHand.size}, currentPlayerId=${state.currentPlayerId}, move.playerId=${move.playerId})")
    
    // Get all occupied positions except the one being undone
    val occupiedPositions = state.board.getOccupiedPositions()
    console.log("UNDO: Total occupied positions on board: ${occupiedPositions.size}")
    
    // DEBUG: Log all jokers on the current board BEFORE undo
    console.log("UNDO: === JOKERS ON BOARD BEFORE UNDO ===")
    for (pos in occupiedPositions) {
        val pin = state.board.getPinAt(pos)
        if (pin != null && pin.isJoker) {
            val (r, c) = pos.toLegacy()
            console.log("UNDO: Joker at ($r,$c): color=${pin.color}, isJokerTurnedBlack=${pin.isJokerTurnedBlack}")
        }
    }
    
    // Create a map of positions to their original pin states for quick lookup
    val originalPinStatesMap = lastMoveOriginalPinStates.toMap()
    
    console.log("UNDO: === ORIGINAL PIN STATES MAP (${lastMoveOriginalPinStates.size} entries) ===")
    lastMoveOriginalPinStates.forEach { (pos, pin) ->
        val (r, c) = pos.toLegacy()
        console.log("UNDO: originalPinStatesMap[($r,$c)] = ${pin.color}, joker=${pin.isJoker}, reversed=${pin.isReversed}, black=${pin.isJokerTurnedBlack}")
    }
    
    // DEBUG: Log the map keys for comparison
    console.log("UNDO: === MAP KEYS ===")
    originalPinStatesMap.keys.forEach { pos ->
        val (r, c) = pos.toLegacy()
        console.log("UNDO: Map key: ($r,$c) -> Position(section=${pos.sectionRow},${pos.sectionCol}, local=${pos.localRow},${pos.localCol})")
    }
    
    // Rebuild the board without the undone pin, restoring original pin states
    // CRITICAL: Also un-blacken jokers if this was a hand-completing move
    var newBoard = Board(initializeDressPins = false)
    
    for (pos in occupiedPositions) {
        if (pos != move.position) {
            var pin = state.board.getPinAt(pos)
            if (pin != null) {
                val (row, col) = pos.toLegacy()
                
                // Check if this pin needs to be restored to its original state
                val originalPin = originalPinStatesMap[pos]
                
                // DEBUG: More detailed logging for jokers
                if (pin.isJoker) {
                    console.log("UNDO: 🃏 Processing joker at ($row,$col)")
                    console.log("UNDO: 🃏 Current state: color=${pin.color}, black=${pin.isJokerTurnedBlack}")
                    console.log("UNDO: 🃏 Position object: section=${pos.sectionRow},${pos.sectionCol}, local=${pos.localRow},${pos.localCol}")
                    console.log("UNDO: 🃏 Found in originalPinStatesMap: ${originalPin != null}")
                    if (originalPin != null) {
                        console.log("UNDO: 🃏 Original state: color=${originalPin.color}, black=${originalPin.isJokerTurnedBlack}")
                    }
                }
                
                if (originalPin != null) {
                    // Restore the pin to its exact original state (before reversal or joker blackening)
                    console.log("UNDO: ✅ Restored pin at ($row,$col) from ${pin.color}(reversed=${pin.isReversed}, black=${pin.isJokerTurnedBlack}) to ${originalPin.color}(reversed=${originalPin.isReversed}, black=${originalPin.isJokerTurnedBlack})")
                    pin = originalPin
                } else if (pin.isJoker && pin.isJokerTurnedBlack) {
                    console.log("UNDO: ⚠️ WARNING: Joker at ($row,$col) is BLACK but NOT in originalPinStatesMap!")
                }
                
                try {
                    newBoard = newBoard.placePinAt(pos, pin)
                } catch (e: Exception) {
                    console.error("Failed to place pin at $pos during undo: ${e.message}")
                }
            }
        }
    }
    
    // DEBUG: Log all jokers on the NEW board AFTER undo
    console.log("UNDO: === JOKERS ON NEW BOARD AFTER UNDO ===")
    val newOccupiedPositions = newBoard.getOccupiedPositions()
    for (pos in newOccupiedPositions) {
        val pin = newBoard.getPinAt(pos)
        if (pin != null && pin.isJoker) {
            val (r, c) = pos.toLegacy()
            console.log("UNDO: Joker at ($r,$c): color=${pin.color}, isJokerTurnedBlack=${pin.isJokerTurnedBlack}")
        }
    }
    
    engine.updateBoardState(newBoard)
    
    // Restore the player's score
    val player = state.players.find { it.id == move.playerId }
    if (player != null) {
        // Calculate the score that was earned from this move (use original board state)
        val scoreResult = ScoreCalculator().calculateMoveScore(state.board, move)
        val restoredScore = (player.score - scoreResult.points).coerceAtLeast(0)
        engine.updatePlayerScores(mapOf(move.playerId to restoredScore))
        
        // Update hand score tracking
        val currentHandScore = playerHandScores.getOrElse(move.playerId) { 0 }
        playerHandScores[move.playerId] = (currentHandScore - scoreResult.points).coerceAtLeast(0)
    }
    
    // CRITICAL: Restore the ORIGINAL pin to the player's hand (before joker conversion)
    // This matches Android's approach - restore the pin as it was in hand, not as it was placed
    console.log("UNDO DEBUG: lastOriginalPin=${lastOriginalPin?.color}(joker=${lastOriginalPin?.isJoker}), move.pin=${move.pin.color}(joker=${move.pin.isJoker})")
    val pinToRestore = lastOriginalPin ?: move.pin
    console.log("UNDO DEBUG: pinToRestore=${pinToRestore.color}(joker=${pinToRestore.isJoker})")
    
    // CRITICAL FIX: If this was a hand-completing move, the hand should be JUST the restored pin
    // Don't add to the new 4-pin hand - replace it entirely
    val restoredHand = if (wasHandCompletingMove) {
        console.log("UNDO: Hand-completing move - restoring hand to just 1 pin")
        listOf(pinToRestore)
    } else {
        // Normal undo - add the pin back to the current hand
        val currentHand = engine.getCurrentGameState()?.currentHand?.toMutableList() ?: mutableListOf()
        currentHand.add(0, pinToRestore) // Add at beginning so it appears first
        currentHand.toList()
    }
    console.log("Restored pin ${pinToRestore.color} (joker=${pinToRestore.isJoker}) to hand, hand size now: ${restoredHand.size}")
    
    // CRITICAL: If this was a hand-completing move, restore the turn FIRST
    // This ensures updateCurrentHand updates the correct player's hand in playerHands
    if (wasHandCompletingMove) {
        console.log("UNDO: Restoring turn to ${move.playerId} BEFORE updating hand")
        engine.syncTurnState(move.playerId)
        gameCurrentPlayerId = move.playerId
        pendingCurrentPlayerId = null
    }
    
    // Update the game engine with the restored hand
    // Now that turn is restored, this will update playerHands[hostId] correctly
    engine.updateCurrentHand(restoredHand)
    
    // Clear the undo state
    lastLocalMove = null
    lastOriginalPin = null
    lastMoveOriginalPinStates = emptyList()
    canUndoCurrentMove = false
    
    // Update UI
    renderBoard()
    renderScores()
    renderHand()
    updateTurnIndicator()  // CRITICAL: Update turn indicator to refresh cursor
    updateBoardInteraction()  // CRITICAL: Update board cursor after undo
    updateUndoButton()
}

/**
 * Handle undo event from another player.
 * 
 * Requirements: 11.5
 */
private fun handleUndoEvent(event: UndoMoveEvent) {
    console.log("=== handleUndoEvent ===")
    console.log("Player ${event.playerId} is undoing their move")
    
    // If it's our own undo event, we already applied it locally
    if (event.playerId == currentPlayerId) {
        console.log("Skipping own undo event - already applied locally")
        return
    }
    
    // CRITICAL: Track undo so we accept the subsequent GameStateUpdate from this player
    // This matches Android's InternetGameAdapter behavior
    lastUndoPlayerId = event.playerId
    lastUndoTimestamp = Date.now()
    console.log("↩️ Tracking undo from ${event.playerId} - will accept GameStateUpdate within ${UNDO_STATE_UPDATE_WINDOW_MS}ms")
    
    // CRITICAL FIX: Cancel hand completion pause when undo is received
    // The undo means the hand is no longer complete, so the pause should stop
    if (isInHandCompletionPause) {
        console.log("Cancelling hand completion pause due to remote undo")
        cancelHandCompletionPause()
    }
}

/**
 * Track a local move for potential undo.
 * Also tracks original pin (before joker conversion) and original pin states (before reversal).
 * This matches Android's approach of storing the complete pin state before modification.
 * 
 * @param move The move that was made
 * @param originalPin The original pin from hand BEFORE joker conversion (for restoring to hand)
 * @param originalPinStates The original states of pins that were reversed by this move
 */
private fun trackMoveForUndo(move: Move, originalPin: Pin, originalPinStates: List<Pair<Position, Pin>> = emptyList()) {
    lastLocalMove = move
    lastOriginalPin = originalPin
    lastMoveOriginalPinStates = originalPinStates
    canUndoCurrentMove = true
    updateUndoButton()
    
    console.log("Tracking move for undo: placed=${move.pin.color}(joker=${move.pin.isJoker}), original=${originalPin.color}(joker=${originalPin.isJoker})")
    
    if (originalPinStates.isNotEmpty()) {
        console.log("Tracking ${originalPinStates.size} original pin states for potential undo")
        originalPinStates.forEach { (pos, pin) ->
            val (row, col) = pos.toLegacy()
            console.log("  Original pin at ($row,$col): ${pin.color}, joker=${pin.isJoker}, reversed=${pin.isReversed}")
        }
    }
}

/**
 * Clear undo tracking (called when turn changes or hand completes).
 */
private fun clearUndoTracking() {
    lastLocalMove = null
    lastOriginalPin = null
    lastMoveOriginalPinStates = emptyList()
    canUndoCurrentMove = false
    updateUndoButton()
}

/**
 * Start the hand completion pause (3 seconds to allow undo).
 * This blocks moves and shows a countdown.
 * Only HOST broadcasts PAUSED state to other players.
 */
private fun startHandCompletionPause(movesCount: Int = -1) {
    if (movesCount > 0) {
        lastHandledPauseMoves = movesCount
    }
    console.log("🎯 Starting hand completion pause (3 seconds) - moves=$movesCount")
    
    // Cancel any existing pause
    handCompletionPauseTimeoutId?.let { window.clearTimeout(it) }
    
    // Set pause state
    isInHandCompletionPause = true
    handCompletionPauseTimeRemaining = 3
    
    // CRITICAL FIX: Only HOST broadcasts PAUSED state
    // CLIENT should NOT broadcast - it receives state from HOST
    // This matches Android's InternetGameAdapter behavior
    if (isHost) {
        val roomId = currentRoomId
        val playerId = currentPlayerId
        val currentState = gameEngine?.getCurrentGameState()
        if (roomId != null && playerId != null && currentState != null) {
            log("🏠 HOST: Broadcasting PAUSED state to other players")
            GameEventManager.broadcastGameStateUpdateForAndroid(
                roomId = roomId,
                playerId = playerId,
                board = currentState.board,
                players = currentState.players,
                currentPlayerIndex = currentState.currentPlayerIndex,
                currentHand = currentState.currentHand,
                gamePhase = "PAUSED",
                playerHands = currentState.playerHands,
                handCount = currentState.handCount
            ) { success, error ->
                if (success) {
                    log("PAUSED state broadcast successful")
                } else {
                    logError("Failed to broadcast PAUSED state: $error")
                }
            }
        }
    } else {
        log("👤 CLIENT: Not broadcasting PAUSED state (only host does this)")
    }
    
    // Update UI to show countdown
    updateTurnIndicator()
    updateUndoButton()
    // renderScoreDisplay() - Integrated into renderScores()
    
    // Start countdown
    runHandCompletionCountdown()
}

/**
 * Run the hand completion countdown timer.
 */
private fun runHandCompletionCountdown() {
    if (handCompletionPauseTimeRemaining <= 0) {
        // Countdown finished
        endHandCompletionPause()
        return
    }
    
    console.log("⏱️ Hand completion pause: $handCompletionPauseTimeRemaining seconds remaining")
    updateTurnIndicator()
    
    // Schedule next tick
    handCompletionPauseTimeoutId = window.setTimeout({
        handCompletionPauseTimeRemaining--
        runHandCompletionCountdown()
    }, 1000)
}

/**
 * End the hand completion pause.
 * Only HOST broadcasts PLAYING state to resume the game for other players.
 * CLIENT just ends its local pause - it will receive state from HOST.
 */
private fun endHandCompletionPause() {
    log("▶️ Hand completion pause ended")
    
    isInHandCompletionPause = false
    handCompletionPauseTimeRemaining = 0
    handCompletionPauseTimeoutId = null
    
    // Apply pending turn switch if any
    if (pendingCurrentPlayerId != null) {
        log("Applying pending turn switch to $pendingCurrentPlayerId")
        gameCurrentPlayerId = pendingCurrentPlayerId
        pendingCurrentPlayerId = null
    }
    
    // Always reset the scores and clear the "last pin" info now that pause is over
    resetHandScore()
    
    // CRITICAL FIX: Only HOST broadcasts GameStateUpdate
    // CLIENT should NOT broadcast - it receives state from HOST
    // This matches Android's InternetGameAdapter behavior
    if (isHost) {
        val roomId = currentRoomId
        val playerId = currentPlayerId
        val currentState = gameEngine?.getCurrentGameState()
        if (roomId != null && playerId != null && currentState != null) {
            log("🏠 HOST: Broadcasting PLAYING state to resume game")
            GameEventManager.broadcastGameStateUpdateForAndroid(
                roomId = roomId,
                playerId = playerId,
                board = currentState.board,
                players = currentState.players,
                currentPlayerIndex = currentState.currentPlayerIndex,
                currentHand = currentState.currentHand,
                gamePhase = "PLAYING",
                playerHands = currentState.playerHands,
                handCount = currentState.handCount
            ) { success, error ->
                if (success) {
                    log("PLAYING state broadcast successful")
                } else {
                    logError("Failed to broadcast PLAYING state: $error")
                }
            }
        }
    } else {
        log("👤 CLIENT: Not broadcasting PLAYING state (only host does this)")
    }
    
    // Clear undo tracking since pause expired
    clearUndoTracking()
    
    // Update UI
    renderBoard() // Ensure board is updated
    renderScores()
    renderHand()
    // renderScoreDisplay() - Integrated into renderScores()
    updateTurnIndicator()
    updateUndoButton()
}

/**
 * Cancel the hand completion pause (e.g., when undo is performed).
 */
private fun cancelHandCompletionPause() {
    console.log("⏹️ Hand completion pause cancelled")
    
    handCompletionPauseTimeoutId?.let { window.clearTimeout(it) }
    isInHandCompletionPause = false
    handCompletionPauseTimeRemaining = 0
    handCompletionPauseTimeoutId = null
    
    // Clear pending turn switch since we are undoing the hand completion
    pendingCurrentPlayerId = null
    
    updateTurnIndicator()
    updateBoardInteraction()  // CRITICAL: Update board cursor after cancelling pause
    updateUndoButton()
    
    // Re-render hand to show available pins again
    renderHand()
}


private fun loadSettingsUI() {
    val pinEl = document.getElementById("setting-pin-score") as? HTMLInputElement
    val handEl = document.getElementById("setting-hand-score") as? HTMLInputElement
    val aiSpeedEl = document.getElementById("setting-ai-speed") as? HTMLInputElement
    val aiSpeedValueEl = document.getElementById("setting-ai-speed-value")
    val maxUndosEl = document.getElementById("setting-max-undos") as? HTMLInputElement
    val maxUndosValueEl = document.getElementById("setting-max-undos-value")
    
    val settings = SettingsManager.getSettings()
    pinEl?.checked = settings.showCurrentPinScore
    handEl?.checked = settings.showCurrentHandScore
    
    // Max Undos Per Hand
    if (maxUndosEl != null) {
        val maxUndos = settings.maxUndosPerHand.coerceIn(0, 4)
        maxUndosEl.value = maxUndos.toString()
        maxUndosValueEl?.textContent = maxUndos.toString()
        
        maxUndosEl.addEventListener("input", {
            val newMaxUndos = maxUndosEl.value.toInt()
            maxUndosValueEl?.textContent = newMaxUndos.toString()
            SettingsManager.setSetting("maxUndosPerHand", newMaxUndos)
            console.log("Max undos per hand set to: $newMaxUndos")
        })
    }
    
    // AI Speed
    if (aiSpeedEl != null) {
        val speed = if (settings.aiSpeed > 0) settings.aiSpeed else 1000
        aiSpeedEl.value = speed.toString()
        aiSpeedValueEl?.textContent = "${speed}ms"
        
        aiSpeedEl.addEventListener("input", {
            val newSpeed = aiSpeedEl.value.toInt()
            aiSpeedValueEl?.textContent = "${newSpeed}ms"
            SettingsManager.setSetting("aiSpeed", newSpeed)
        })
    }
    
    pinEl?.addEventListener("change", { 
        SettingsManager.setSetting("showCurrentPinScore", pinEl.checked)
        showCurrentPinScore = pinEl.checked
    })
    handEl?.addEventListener("change", { 
        SettingsManager.setSetting("showCurrentHandScore", handEl.checked)
        showCurrentHandScore = handEl.checked
    })
}

private fun loadSettings() {
    SettingsManager.initialize()
    SoundManager.initialize()  // Initialize sound manager after settings
    val settings = SettingsManager.getSettings()
    showCurrentPinScore = settings.showCurrentPinScore
    showCurrentHandScore = settings.showCurrentHandScore
}

private fun saveSettings() {
    SettingsManager.setSetting("showCurrentPinScore", showCurrentPinScore)
    SettingsManager.setSetting("showCurrentHandScore", showCurrentHandScore)
}

// Main Entry Point
fun main() {
    console.log("Pinpoint Web Client initializing...")
    
    // FIRST: Detect platform environment (Discord, PlayGama, or standalone)
    co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.detectEnvironment()
    
    // SECOND: Initialize PlayGama Bridge if available
    initializePlayGamaBridge()
    
    // Set version info
    val version = "v1.0.0"
    document.getElementById("version-info")?.textContent = version
    document.getElementById("main-menu-version")?.textContent = version
    
    exportFunctionsToWindow()
    setupNavigationCallbacks()
    setupRoomManagerCallbacks()
    setupGameEventManagerCallbacks()
    
    // Initialize RewardedAdManager
    console.log("🚀 MAIN: About to call RewardedAdManager.initialize()")
    RewardedAdManager.initialize()
    console.log("🚀 MAIN: RewardedAdManager.initialize() completed")
    
    // Initialize AIGameManager for single-player mode
    console.log("🚀 MAIN: About to call AIGameManager.initialize()")
    AIGameManager.initialize()
    console.log("🚀 MAIN: AIGameManager.initialize() completed")
    
    // Initialize LocalGameManager for local multiplayer
    console.log("🚀 MAIN: About to call LocalGameManager.initialize()")
    LocalGameManager.initialize()
    console.log("🚀 MAIN: LocalGameManager.initialize() completed")
    
    // Setup responsive canvas sizing
    setupResponsiveCanvas()
    
    try {
        // Check if Firebase is available (may not be on some platforms like PlayGama QA)
        if (js("typeof firebase !== 'undefined'") as Boolean) {
            firebaseAvailable = true
            val config = js("""({
                apiKey: '$FIREBASE_API_KEY',
                projectId: '$FIREBASE_PROJECT_ID',
                databaseURL: '$FIREBASE_DATABASE_URL'
            })""")
            firebase.initializeApp(config)
            database = firebase.database()
            FirebaseRoomManager.database = database
            SharedBoardManager.database = database
            console.log("Firebase initialized")
            
            // Set up auth state listener to track when auth is ready
            firebase.auth().onAuthStateChanged(fun(user: dynamic): dynamic {
                if (user != null) {
                    console.log("✅ Firebase auth state: signed in as ${user.uid}")
                    isFirebaseAuthReady = true
                } else {
                    console.log("⏳ Firebase auth state: not signed in, signing in anonymously...")
                    isFirebaseAuthReady = false
                    // Sign in anonymously
                    firebase.auth().signInAnonymously().catch(fun(error: dynamic): dynamic {
                        console.error("❌ Firebase anonymous auth failed:", error.message)
                        window.alert("Failed to connect to game server. Please refresh the page.")
                        return Unit.asDynamic()
                    })
                }
                return Unit.asDynamic()
            })
        } else {
            console.log("⏳ Firebase not available (standalone/PlayGama mode)")
        }
    } catch (e: Exception) { console.error("Firebase failed:", e) }
    try {
        val options = js("({ cluster: '$PUSHER_CLUSTER' })")
        pusher = Pusher(PUSHER_KEY, options)
        console.log("Pusher initialized")
    } catch (e: Exception) { console.error("Pusher failed:", e) }
    loadSettings()
    console.log("Pinpoint Web Client ready")
}

    /* ==========================================
       Leaderboard UI
       ========================================== */
    
    // Store current period
    private var currentLeaderboardPeriod = "allTime"
    
    private fun loadLeaderboard() {
        val container = document.getElementById("leaderboard-list") as? HTMLElement ?: return
        
        // Update tab visual state
        document.getElementById("tab-weekly")?.classList?.remove("active")
        document.getElementById("tab-all-time")?.classList?.remove("active")
        document.getElementById("tab-best")?.classList?.remove("active")
        
        when(currentLeaderboardPeriod) {
            "weekly" -> document.getElementById("tab-weekly")?.classList?.add("active")
            "allTime" -> document.getElementById("tab-all-time")?.classList?.add("active")
            "best" -> document.getElementById("tab-best")?.classList?.add("active")
        }
        
        container.innerHTML = "<div class=\"loading\">Loading leaderboard...</div>"
        
        // Special handling for "best" tab - show both daily and all-time best
        if (currentLeaderboardPeriod == "best") {
            loadBestScoresTab(container)
            return
        }
        
        FirebaseRoomManager.fetchLeaderboard(currentLeaderboardPeriod) { entries ->
            if (entries.isEmpty()) {
                container.innerHTML = "<div class=\"no-data\">No scores yet for this period!</div>"
                return@fetchLeaderboard
            }
            
            val listHtml = StringBuilder()
            val isDaily = currentLeaderboardPeriod == "dailyBest"
            
            // Add table header - hide Games/Wins for daily (only shows best score)
            listHtml.append("<div class=\"leaderboard-header-row\">")
            listHtml.append("<div class=\"col-rank\">#</div>")
            listHtml.append("<div class=\"col-player\">Player</div>")
            if (!isDaily) {
                listHtml.append("<div class=\"col-games\">Games</div>")
                listHtml.append("<div class=\"col-wins\">Wins</div>")
            }
            listHtml.append("<div class=\"col-score\">Score</div>")
            listHtml.append("</div>")
            
            entries.forEachIndexed { index, entry ->
                val rank = index + 1
                val rowClass = if (entry.playerId == currentPlayerId) "leaderboard-row current-user" else "leaderboard-row"
                val rankClass = when(rank) {
                    1 -> "rank-1"
                    2 -> "rank-2"
                    3 -> "rank-3"
                    else -> ""
                }
                
                listHtml.append("<div class=\"$rowClass\">")
                listHtml.append("<div class=\"col-rank $rankClass\">$rank</div>")
                listHtml.append("<div class=\"col-player\">${entry.displayName}</div>")
                if (!isDaily) {
                    listHtml.append("<div class=\"col-games\">${entry.gamesPlayed}</div>")
                    listHtml.append("<div class=\"col-wins\">${entry.wins}</div>")
                }
                listHtml.append("<div class=\"col-score\">")
                listHtml.append("<span class=\"score-main\">${entry.totalScore}</span>")
                if (!isDaily && entry.highestScore > 0) {
                    listHtml.append("<span class=\"score-best\">Best: ${entry.highestScore}</span>")
                }
                listHtml.append("</div>")
                listHtml.append("</div>")
            }
            container.innerHTML = listHtml.toString()
        }
    }
    
    private fun loadBestScoresTab(container: HTMLElement) {
        // Load both daily best and all-time best
        var dailyLoaded = false
        var allTimeLoaded = false
        val htmlParts = mutableMapOf<String, String>()
        
        fun checkAndRender() {
            if (dailyLoaded && allTimeLoaded) {
                val finalHtml = """
                    <h3 style="margin: 20px 0 10px 0; color: #4CAF50;">Today's Best</h3>
                    ${htmlParts["daily"] ?: "<div class=\"no-data\">No scores today</div>"}
                    <h3 style="margin: 30px 0 10px 0; color: #2196F3;">All-Time Best</h3>
                    ${htmlParts["allTime"] ?: "<div class=\"no-data\">No all-time scores</div>"}
                """.trimIndent()
                container.innerHTML = finalHtml
            }
        }
        
        // Load daily best
        FirebaseRoomManager.fetchLeaderboard("dailyBest") { entries ->
            htmlParts["daily"] = if (entries.isEmpty()) {
                "<div class=\"no-data\">No scores today</div>"
            } else {
                buildBestScoreTable(entries)
            }
            dailyLoaded = true
            checkAndRender()
        }
        
        // Load all-time best
        FirebaseRoomManager.fetchLeaderboard("allTimeBest") { entries ->
            htmlParts["allTime"] = if (entries.isEmpty()) {
                "<div class=\"no-data\">No all-time scores</div>"
            } else {
                buildBestScoreTable(entries)
            }
            allTimeLoaded = true
            checkAndRender()
        }
    }
    
    private fun buildBestScoreTable(entries: List<LeaderboardEntry>): String {
        val listHtml = StringBuilder()
        
        // Header
        listHtml.append("<div class=\"leaderboard-header-row\">")
        listHtml.append("<div class=\"col-rank\">#</div>")
        listHtml.append("<div class=\"col-player\">Player</div>")
        listHtml.append("<div class=\"col-score\">Best Score</div>")
        listHtml.append("</div>")
        
        // Rows
        entries.forEachIndexed { index, entry ->
            val rank = index + 1
            val rowClass = if (entry.playerId == currentPlayerId) "leaderboard-row current-user" else "leaderboard-row"
            val rankClass = when(rank) {
                1 -> "rank-1"
                2 -> "rank-2"
                3 -> "rank-3"
                else -> ""
            }
            
            listHtml.append("<div class=\"$rowClass\">")
            listHtml.append("<div class=\"col-rank $rankClass\">$rank</div>")
            listHtml.append("<div class=\"col-player\">${entry.displayName}</div>")
            listHtml.append("<div class=\"col-score\">")
            listHtml.append("<span class=\"score-main\">${entry.totalScore}</span>")
            listHtml.append("</div>")
            listHtml.append("</div>")
        }
        
        return listHtml.toString()
    }

private fun exportFunctionsToWindow() {
    val w = window.asDynamic()
    
    // Initialize managers
    LocalGameManager.initialize()
    
    // Leaderboard functions
    w.pinpointSwitchLeaderboard = { period: String ->
        currentLeaderboardPeriod = period
        loadLeaderboard()
    }
    
    // Global exports for simpler HTML access
    w.leaveRoom = { leaveRoom() }
    w.joinRoom = { id: String -> joinRoom(id) }
    w.startGame = { startGame() }
    w.restoreSavedGame = { jsRestoreSavedGame() }
    w.startNewGame = { jsStartNewGame() }
    w.deleteSavedGame = { jsDeleteSavedGame() }
    
    w.forceResetRoom = { forceResetRoom() }
    
    // Export NavigationManager for HTML onclick handlers
    w.NavigationManager = js("{}")
    w.NavigationManager.navigateTo = { screen: String -> NavigationManager.navigateTo(screen) }
    w.NavigationManager.goBack = { NavigationManager.goBack() }
    
    // Export FirebaseRoomManager for HTML onclick handlers
    w.FirebaseRoomManager = js("{}")
    w.FirebaseRoomManager.leaveRoom = { leaveRoom() }  // Use Main.kt wrapper for proper cleanup
    w.FirebaseRoomManager.startGame = { FirebaseRoomManager.startGame() }
    w.FirebaseRoomManager.forceResetRoom = { id: String -> forceResetRoom() } // Added for convenience
    
    // AI Player functions
    w.showAIDifficultyDialog = { showAIDifficultyDialog() }
    w.pinpointAddAIPlayer = { diff: String -> addAIPlayer(diff) }
    w.pinpointRemoveAIPlayer = { id: String -> removeAIPlayer(id) }
    w.removeAIPlayer = { id: String -> removeAIPlayer(id) }  // Direct export for HTML
    w.pinpointCancelAIDialog = { cancelAIDialog() }
    w.cancelAIDialog = { cancelAIDialog() }  // Direct export for HTML
    
    w.pinpointNavigateTo = { screen: String -> navigateTo(screen) }
    w.pinpointGoBack = { goBack() }
    w.pinpointJoinRoom = { roomId: String -> joinRoom(roomId) }
    w.pinpointLeaveRoom = { leaveRoom() }
    w.pinpointStartGame = { startGame() }
    w.pinpointForceResetRoom = { forceResetRoom() }
    w.pinpointTestGame = { startTestGame() }  // Debug function
    w.pinpointSelectPin = { index: Int -> selectPin(index) }
    w.selectHandPin = { index: Int -> selectPin(index) }  // Alias for HTML onclick
    w.pinpointSelectJokerColor = { color: String -> selectJokerColor(color) }
    w.pinpointCancelJoker = { cancelJoker() }
    // New joker functions from hand UI
    w.sacrificePinForJoker = { index: Int -> sacrificePinForJoker(index) }
    w.selectJokerColorFromHand = { color: String -> selectJokerColorFromHand(color) }
    w.cancelJokerFromHand = { cancelJokerFromHand() }
    // Undo functionality
    w.pinpointRequestUndo = { requestUndo() }
    w.pinpointWatchAdForCredit = { pinpointWatchAdForCredit() }
    // Clear all rooms (debug)
    w.clearAllRooms = { clearAllRoomsDebug() }
    // Shared board joker function
    w.sharedBoardSacrificePinForJoker = { index: Int -> sharedBoardSacrificePinForJoker(index) }
    // Shared board pin selection
    w.selectPinFromSharedHand = { index: Int -> selectPinFromSharedHand(index) }
    // AdSense H5 Bridge
    w.H5AdManager = js("{}")
    w.H5AdManager.triggerAdBreak = { type: String, name: String, before: dynamic, after: dynamic, dismissed: dynamic, viewed: dynamic, done: dynamic ->
        H5AdManager.triggerAdBreakWithFeedback(
            type = type,
            name = name,
            onBeforeAd = { if (before != null && before != undefined) before() },
            onAfterAd = { if (after != null && after != undefined) after() },
            onAdDismissed = { if (dismissed != null && dismissed != undefined) dismissed() },
            onAdViewed = { if (viewed != null && viewed != undefined) viewed() },
            onAdBreakDone = { placementInfo -> if (done != null && done != undefined) done(placementInfo) },
            onAdUnavailable = { 
                console.log("Main.kt: onAdUnavailable triggered - handling session unlock")
                // Handle session unlock for AI difficulties
                if (type == "reward" && name.startsWith("unlock_")) {
                    val difficulty = name.removePrefix("unlock_")
                    console.log("Main.kt: Unlocking $difficulty for session")
                    // Set temporary unlock in JavaScript
                    val w = window.asDynamic()
                    if (w.temporaryDifficultyUnlocks == null) {
                        w.temporaryDifficultyUnlocks = js("{}")
                    }
                    w.temporaryDifficultyUnlocks[difficulty] = true
                    console.log("Main.kt: Session unlock set for $difficulty")
                    
                    // Trigger the done callback with a fake "dismissed" status to trigger fallback logic
                    if (done != null && done != undefined) {
                        val fakeInfo = js("{}")
                        fakeInfo.breakStatus = "dismissed"
                        done(fakeInfo)
                    }
                } else {
                    if (after != null && after != undefined) after() 
                }
            }
        )
    }

    console.log("Pinpoint functions exported to window")
    // Signal to JS that Kotlin is ready
    val onReady = w.onKotlinReady
    if (onReady != null) {
        onReady()
    }
}



// Debug function to start a local test game without multiplayer
private var testPlayerId = "test_player_1"

private fun startTestGame() {
    console.log("Starting test game...")
    NavigationManager.navigateTo(Screen.GAME)
    
    val seed = Date.now().toLong()
    val player1 = Player(id = "test_player_1", name = "You")
    val player2 = Player(id = "test_player_2", name = "Opponent")
    val playerList = listOf(player1, player2)
    
    gamePlayers = playerList.associateBy { it.id }
    testPlayerId = player1.id
    
    val config = GameConfig(
        players = playerList,
        startingPlayerId = player1.id,
        boardSeed = seed,
        isNetworkClient = false  // Local game
    )
    
    val engine = GameEngine(listener = object : GameEngineListener {
        override fun onGameStarted(state: GameState) { 
            renderBoard()
            renderScoresTest()
            renderHandTest()
        }
        override fun onMoveApplied(state: GameState, move: Move) { 
            renderBoard()
            renderScoresTest()
            renderHandTest()
            // Auto-switch turns for testing
            gameCurrentPlayerId = state.currentPlayerId
            updateTurnIndicatorTest()
        }
        override fun onGameEnded(state: GameState) { 
            console.log("Test game ended")
        }
    })
    
    gameEngine = engine
    engine.startGame(config)
    gameCurrentPlayerId = player1.id
    updateTurnIndicatorTest()
    setupTestBoardClick()
    
    console.log("Test game started! Click on the board to place pins.")
}

private fun renderScoresTest() {
    val panel = document.getElementById("game-scores") ?: return
    val state = gameEngine?.getCurrentGameState() ?: return
    val html = StringBuilder()
    for (player in state.players) {
        val isCurrentPlayer = player.id == testPlayerId
        val isCurrentTurn = player.id == gameCurrentPlayerId
        val itemClass = buildString {
            append("score-item")
            if (isCurrentPlayer) append(" current-player")
            if (isCurrentTurn) append(" active-turn")
        }
        html.append("<div class=\"$itemClass\">")
        html.append("<div class=\"score-name\">${player.name}${if (isCurrentPlayer) " (You)" else ""}</div>")
        html.append("<div class=\"score-value\">${player.score}</div>")
        html.append("</div>")
    }
    panel.innerHTML = html.toString()
}

private fun updateTurnIndicatorTest() {
    val indicator = document.getElementById("game-turn-indicator") ?: return
    val isMyTurn = gameCurrentPlayerId == testPlayerId
    val playerName = gamePlayers[gameCurrentPlayerId]?.name ?: "?"
    if (isMyTurn) {
        indicator.textContent = "Your turn!"
        indicator.classList.add("my-turn")
    } else {
        indicator.textContent = "$playerName's turn"
        indicator.classList.remove("my-turn")
    }
    val canvas = document.getElementById("game-board") as? HTMLCanvasElement ?: return
    canvas.style.cursor = "pointer"  // Always allow clicks in test mode
}

private fun renderHandTest() {
    val container = document.getElementById("hand-pins") ?: return
    val handContainer = document.getElementById("player-hand") ?: return
    val state = gameEngine?.getCurrentGameState() ?: return
    val isMyTurn = gameCurrentPlayerId == testPlayerId
    
    if (isMyTurn) {
        handContainer.classList.remove("not-your-turn")
    } else {
        handContainer.classList.add("not-your-turn")
    }
    
    val availablePins = state.getAvailablePins()
    if (availablePins.isEmpty()) {
        container.innerHTML = "<div style=\"color: #aaa;\">No pins available</div>"
        return
    }
    
    val html = StringBuilder()
    
    // Check if we're in joker creation mode
    val sacrificeIndex = pinToSacrificeIndex
    if (sacrificeIndex != null && sacrificeIndex in availablePins.indices) {
        // Show joker color selection UI
        val sacrificedPin = availablePins[sacrificeIndex]
        html.append("<div class=\"joker-selection\">")
        html.append("<div class=\"joker-selection-title\">Playing ${sacrificedPin.color.name} as joker</div>")
        html.append("<div class=\"joker-selection-subtitle\">Choose color:</div>")
        html.append("<div class=\"joker-color-row\">")
        listOf("RED", "GREEN", "PURPLE", "YELLOW").forEach { color ->
            val bgColor = when(color) {
                "RED" -> "#FF6B6B"
                "GREEN" -> "#00FF00"
                "PURPLE" -> "#8B00FF"
                "YELLOW" -> "#FFFF00"
                else -> "#666"
            }
            val textColor = if (color == "GREEN" || color == "YELLOW") "#000" else "#fff"
            html.append("<button class=\"joker-color-option\" style=\"background:$bgColor;color:$textColor\" onclick=\"selectJokerColorFromHand('$color')\">$color</button>")
        }
        html.append("</div>")
        html.append("<button class=\"joker-cancel-btn\" onclick=\"cancelJokerFromHand()\">Cancel</button>")
        html.append("</div>")
    } else {
        // Normal pin display with joker buttons
        availablePins.forEachIndexed { index, pin ->
            val isSelected = index == selectedPinIndex
            val colorClass = if (pin.isJoker) "joker" else pin.color.name.lowercase()
            val selectedClass = if (isSelected) " selected" else ""
            val label = if (pin.isJoker) "J" else ""
            
            html.append("<div class=\"hand-pin-container\">")
            html.append("<div class=\"hand-pin $colorClass$selectedClass\" onclick=\"selectHandPin($index)\">")
            html.append(label)
            html.append("</div>")
            // Add JOKER button under each non-joker pin
            if (!pin.isJoker && isMyTurn) {
                html.append("<button class=\"joker-btn\" onclick=\"sacrificePinForJoker($index)\">JOKER</button>")
            }
            html.append("</div>")
        }
    }
    container.innerHTML = html.toString()
}

private var testBoardClickAttached = false

private fun setupTestBoardClick() {
    val canvas = document.getElementById("game-board") as? HTMLCanvasElement ?: return
    if (testBoardClickAttached) return
    
    canvas.addEventListener("click", { event ->
        handleTestBoardClick(event as MouseEvent)
    })
    testBoardClickAttached = true
}

private fun handleTestBoardClick(event: MouseEvent) {
    val canvas = document.getElementById("game-board") as? HTMLCanvasElement ?: return
    val engine = gameEngine ?: return
    val state = engine.getCurrentGameState() ?: return
    
    val rect = canvas.getBoundingClientRect()
    val cellSize = canvas.width / 10.0
    val x = event.clientX - rect.left
    val y = event.clientY - rect.top
    val col = (x / cellSize).toInt().coerceIn(0, 9)
    val row = (y / cellSize).toInt().coerceIn(0, 9)
    val position = Position.fromLegacy(row, col)
    
    if (!state.board.isEmpty(position)) {
        console.log("Cell occupied")
        return@handleTestBoardClick
    }
    
    val currentPlayer = gameCurrentPlayerId ?: return@handleTestBoardClick
    
    // Check if we have a pending joker from the hand UI
    val jokerPin = pendingJokerPin
    if (jokerPin != null) {
        console.log("Placing joker ${jokerPin.originalColor} as ${jokerPin.color} at ($row, $col) for $currentPlayer")
        val move = Move(currentPlayer, jokerPin, position)
        engine.applyRemoteMove(move)
        selectedPinIndex = 0
        pendingJokerPin = null
        pinToSacrificeIndex = null
        return@handleTestBoardClick
    }
    
    val availablePins = state.getAvailablePins()
    if (availablePins.isEmpty()) {
        console.log("No pins available")
        return@handleTestBoardClick
    }
    
    // Use selected pin from hand
    val pinIndex = selectedPinIndex.coerceIn(0, availablePins.size - 1)
    val pin = availablePins[pinIndex]
    
    // If it's already a joker pin in hand, show color picker dialog (legacy path)
    if (pin.isJoker) {
        showJokerDialogTest(pin, position)
        return@handleTestBoardClick
    }
    
    console.log("Placing ${pin.color} pin at ($row, $col) for $currentPlayer")
    val move = Move(currentPlayer, pin, position)
    engine.applyRemoteMove(move)
    selectedPinIndex = 0  // Reset selection
}

private fun showJokerDialogTest(pin: Pin, position: Position) {
    pendingJokerPin = pin
    pendingJokerPosition = position
    val dialog = document.getElementById("joker-dialog") as? HTMLElement ?: return
    dialog.style.display = "flex"
    
    // Set up color button click handlers for test mode
    val buttons = document.querySelectorAll(".joker-color-btn")
    for (i in 0 until buttons.length) {
        val btn = buttons.item(i) as? HTMLElement ?: continue
        val color = btn.getAttribute("data-color") ?: continue
        btn.onclick = { selectJokerColorTest(color) }
    }
}

private fun selectJokerColorTest(colorName: String) {
    val pin = pendingJokerPin ?: return
    val position = pendingJokerPosition ?: return
    val engine = gameEngine ?: return
    val currentPlayer = gameCurrentPlayerId ?: return
    
    val jokerColor = try {
        PinColor.valueOf(colorName)
    } catch (e: Exception) {
        console.error("Invalid joker color: $colorName")
        return
    }
    
    val jokerWithColor = Pin.joker(originalColor = pin.color, playedAsColor = jokerColor)
    
    console.log("Placing joker as $colorName at $position for $currentPlayer")
    hideJokerDialog()
    
    val move = Move(currentPlayer, jokerWithColor, position)
    engine.applyRemoteMove(move)
    selectedPinIndex = 0
}


// ==========================================
// AI Player Functions
// ==========================================

@JsExport
@JsName("pinpointShowAIDifficultyDialog")
fun showAIDifficultyDialog() {
    // Show AI difficulty selection dialog
    val dialog = document.getElementById("ai-difficulty-dialog")
    dialog?.asDynamic()?.style?.display = "flex"
    
    // Refresh locks in JS
    js("if (window.refreshAIDifficultyLocks) window.refreshAIDifficultyLocks();")
}

@JsExport
@JsName("cancelAIDialog")
fun cancelAIDialog() {
    // Hide AI difficulty selection dialog
    val dialog = document.getElementById("ai-difficulty-dialog")
    dialog?.asDynamic()?.style?.display = "none"
}

/**
 * Check for saved game and show restore UI if available.
 * Called when navigating to main menu.
 */
private fun checkForSavedGame() {
    PlayGamaStorageManager.loadGameState { jsonString ->
        if (jsonString != null && jsonString.isNotEmpty()) {
            val state = GameSaveState.fromJson(jsonString)
            if (state != null && state.players.isNotEmpty()) {
                console.log("💾 Saved game found, showing restore UI (${state.players.size} players, ${state.boardPins.size} pins)")
                val metadata = SaveMetadata(
                    timestamp = state.lastSaveTimestamp,
                    playerNames = state.players.map { it.name },
                    gameMode = state.gameMode,
                    playerCount = state.players.size
                )
                showRestoreGameUI(metadata)
                return@loadGameState
            }
        }
        console.log("ℹ️ No saved game found")
        hideRestoreGameUI()
    }
}

/**
 * Show restore game UI
 */
private fun showRestoreGameUI(metadata: SaveMetadata? = null) {
    val savedSection = document.getElementById("saved-game-section") as? HTMLElement
    savedSection?.style?.display = "block"
    
    val landingSavedSection = document.getElementById("landing-saved-game-section") as? HTMLElement
    landingSavedSection?.style?.display = "block"
    
    if (metadata != null) {
        val modeLabel = if (metadata.gameMode == "AI") "vs AI" else "${metadata.playerCount} Players"
        val text = "Saved Game ($modeLabel) • Last played: ${metadata.getTimeAgo()}"
        
        val textElem = document.getElementById("saved-game-text") as? HTMLElement
        textElem?.textContent = text
        
        val landingTextElem = document.getElementById("landing-saved-game-text") as? HTMLElement
        landingTextElem?.textContent = text
    }
}

/**
 * Hide restore game UI
 */
private fun hideRestoreGameUI() {
    val savedSection = document.getElementById("saved-game-section") as? HTMLElement
    savedSection?.style?.display = "none"
    
    val landingSavedSection = document.getElementById("landing-saved-game-section") as? HTMLElement
    landingSavedSection?.style?.display = "none"
}

/**
 * Restore saved game (JS-callable)
 */
@JsExport
@JsName("restoreSavedGame")
fun jsRestoreSavedGame() {
    console.log("🔄 Restoring saved game...")
    
    PlayGamaStorageManager.loadGameState { jsonString ->
        if (jsonString == null) {
            console.log("ℹ️ No saved game to restore")
            return@loadGameState
        }
        
        val state = GameSaveState.fromJson(jsonString)
        if (state == null) {
            console.error("❌ Failed to parse saved game state")
            window.alert("Failed to load saved game.")
            return@loadGameState
        }
        
        console.log("✅ Loaded saved game: ${state.gameMode}, ${state.players.size} players")
        
        // Restore based on game mode
        when (state.gameMode) {
            "LOCAL_MULTIPLAYER", "AI" -> {
                console.log("🔄 Restoring game (${state.gameMode})")
                LocalGameManager.restoreGame(state)
                NavigationManager.navigateTo(Screen.LOCAL_GAME)
            }
            else -> {
                console.log("🔄 Restoring local game for mode: ${state.gameMode}")
                LocalGameManager.restoreGame(state)
                NavigationManager.navigateTo(Screen.LOCAL_GAME)
            }
        }
    }
}

/**
 * Start new game (clears saved game, JS-callable)
 */
@JsExport
@JsName("startNewGame")
fun jsStartNewGame() {
    console.log("🗑️ Starting new game (clearing saved game)...")
    
    PlayGamaStorageManager.clearSavedGame { success ->
        if (success) {
            console.log("🗑️ Saved game cleared")
        }
        hideRestoreGameUI()
        NavigationManager.navigateTo(Screen.LOCAL_SETUP)
    }
}

/**
 * Delete saved game and return to main menu
 */
@JsExport
@JsName("deleteSavedGame")
fun jsDeleteSavedGame() {
    console.log("🗑️ Deleting saved game...")

    PlayGamaStorageManager.clearSavedGame { success ->
        if (success) {
            console.log("🗑️ Saved game cleared successfully")
        } else {
            console.warn("⚠️ clearSavedGame reported failure, continuing anyway")
        }
        // Hide the saved-game UI panels
        hideRestoreGameUI()
        // Navigate back to main menu (shows multiplayer / single player options)
        NavigationManager.navigateTo(Screen.MAIN_MENU)
    }
}


fun addAIPlayer(difficulty: String) {
    // Hide dialog
    val dialog = document.getElementById("ai-difficulty-dialog")
    dialog?.asDynamic()?.style?.display = "none"
    
    // Add AI player to room
    val diff = difficulty.uppercase()
    FirebaseRoomManager.addAIPlayer(diff) { success, error ->
        if (!success) {
            window.alert(error ?: "Failed to add AI player")
        }
    }
}

fun removeAIPlayer(playerId: String) {
    if (window.confirm("Remove this AI player?")) {
        FirebaseRoomManager.removeAIPlayer(playerId) { success, error ->
            if (!success) {
                window.alert(error ?: "Failed to remove AI player")
            }
        }
    }
}
fun pinpointWatchAdForCredit() {
    // Proactively unlock audio on ad request (valid user gesture)
    SoundManager.unlockAudio()
    
    RewardedAdManager.showRewardedAd(
        onRewardEarned = {
            console.log("Main: Ad completed, adding undo credit")
            // Update all game managers to refresh their UI
            updateUndoButtonFromSharedState()
            // In internet play, we don't currently use credits, but we track them anyway
        },
        onAdClosed = {
            console.log("Main: Ad closed")
        },
        onAdFailed = {
            console.log("Main: Ad failed")
        }
    )
}


/**
 * Send game ready message to PlayGama platform.
 * This signals that the game has fully loaded and is ready to play.
 * PlayGama uses this to hide loading screens and start analytics.
 */
private fun sendPlayGamaGameReady() {
    try {
        val bridge: dynamic = js("window.bridge")
        if (bridge == null) {
            console.log("ℹ️ PlayGama Bridge not available - skipping game_ready message")
            return
        }
        
        // Try bridge.platform.sendMessage('game_ready') - standard API
        val platform: dynamic = bridge.platform
        if (platform != null && js("typeof platform.sendMessage === 'function'")) {
            console.log("📢 Sending game_ready message to PlayGama...")
            platform.sendMessage("game_ready")
            console.log("✅ game_ready message sent successfully")
            return
        }
        
        // Fallback: try window.platform.sendMessage (some SDK versions)
        val windowPlatform: dynamic = js("window.platform")
        if (windowPlatform != null && js("typeof windowPlatform.sendMessage === 'function'")) {
            console.log("📢 Sending game_ready message to PlayGama (via window.platform)...")
            windowPlatform.sendMessage("game_ready")
            console.log("✅ game_ready message sent successfully")
            return
        }
        
        console.warn("⚠️ PlayGama platform.sendMessage not available")
    } catch (e: Exception) {
        console.warn("⚠️ Failed to send game_ready message: ${e.message}")
    }
}

/**
 * Initialize PlayGama Bridge and send game ready signal.
 * This ensures the PlayGama QA tool receives the initialization message.
 */
private fun initializePlayGamaBridge() {
    try {
        val hasBridge = js("typeof window.bridge !== 'undefined'") as Boolean
        if (!hasBridge) {
            console.log("ℹ️ PlayGama Bridge not available (standalone mode)")
            checkForSavedGame()
            return
        }
        
        console.log("🎮 PlayGama Bridge detected - calling initialize()...")
        val bridge: dynamic = js("window.bridge")
        
        if (bridge != null) {
            val initPromise: dynamic = bridge.initialize()
            if (initPromise != null && js("typeof initPromise.then === 'function'")) {
                initPromise.then({
                    console.log("✅ PlayGama Bridge initialized")
                    
                    // Set bridge initialized and recheck storage
                    PlayGamaStorageManager.setBridgeInitialized()
                    
                    // In PlayGama, transition to Main Menu
                    if (NavigationManager.getCurrentScreen() == Screen.LANDING) {
                        NavigationManager.navigateTo(Screen.MAIN_MENU)
                    }
                    
                    // Check for saved games after bridge is initialized
                    checkForSavedGame()
                    
                    // Send game ready message after bridge is fully initialized
                    window.setTimeout({
                        sendPlayGamaGameReady()
                    }, 100)
                })
            } else {
                console.log("✅ PlayGama Bridge initialize() called (no promise returned)")
                
                PlayGamaStorageManager.setBridgeInitialized()
                
                if (NavigationManager.getCurrentScreen() == Screen.LANDING) {
                    NavigationManager.navigateTo(Screen.MAIN_MENU)
                }
                
                checkForSavedGame()
                
                // Send game ready message immediately if no promise
                window.setTimeout({
                    sendPlayGamaGameReady()
                }, 100)
            }
        }
    } catch (e: Exception) {
        console.warn("⚠️ PlayGama initialization failed: ${e.message}")
        checkForSavedGame()
    }
}
